import "core-js/modules/es6.array.find-index";
import "core-js/modules/es7.object.values";
import "core-js/modules/es6.array.reduce";
import "core-js/modules/es6.regexp.match";
import "core-js/modules/es6.regexp.split";
import "core-js/modules/es6.array.some";
import "core-js/modules/es6.array.index-of";
import "core-js/modules/es7.symbol.async-iterator";
import "core-js/modules/es6.symbol";
import "core-js/modules/es6.array.sort";
import "core-js/modules/es6.array.from";
import "core-js/modules/es6.array.filter";
import "core-js/modules/es6.object.keys";
import "core-js/modules/es6.array.map";
import "core-js/modules/es6.string.starts-with";
import "core-js/modules/es6.array.find";
import "core-js/modules/es6.array.is-array";
import "core-js/modules/es6.map";
import "core-js/modules/es6.promise";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.string.iterator";
import "core-js/modules/es6.set";
import "core-js/modules/es6.array.for-each";
import { __assign, __spreadArrays, __rest, __extends } from 'tslib';
import { useRef, createContext, useContext, useMemo, useEffect, createElement, forwardRef, Fragment, Component, useState, useCallback, cloneElement, Children, isValidElement } from 'react';
import sync, { getFrameData, cancelSync } from 'framesync';
import { velocityPerSecond, clamp, distance, mix, progress, mixColor, interpolate, wrap } from '@popmotion/popcorn';
import styler, { createStylerFactory, buildStyleProperty, isTransformProp, transformProps, buildSVGAttrs } from 'stylefire';
import { color, complex, number, px, percent, degrees, vw, vh } from 'style-value-types';
import { action, delay, tween, spring, keyframes as keyframes$1, inertia } from 'popmotion';
import { invariant, warning } from 'hey-listen';
import * as easingLookup from '@popmotion/easing';
import { cubicBezier, linear, circOut } from '@popmotion/easing';

var isFloat = function isFloat(value) {
  return !isNaN(parseFloat(value));
};
/**
 * `MotionValue` is used to track the state and velocity of motion values.
 *
 * @public
 */


var MotionValue =
/** @class */
function () {
  /**
   * @param init - The initiating value
   * @param config - Optional configuration options
   *
   * -  `transformer`: A function to transform incoming values with.
   *
   * @internal
   */
  function MotionValue(init) {
    var _this = this;
    /**
     * Duration, in milliseconds, since last updating frame.
     *
     * @internal
     */


    this.timeDelta = 0;
    /**
     * Timestamp of the last time this `MotionValue` was updated.
     *
     * @internal
     */

    this.lastUpdated = 0;
    /**
     * Tracks whether this value can output a velocity. Currently this is only true
     * if the value is numerical, but we might be able to widen the scope here and support
     * other value types.
     *
     * @internal
     */

    this.canTrackVelocity = false;

    this.updateAndNotify = function (v, render) {
      if (render === void 0) {
        render = true;
      }

      _this.prev = _this.current;
      _this.current = v;

      if (_this.updateSubscribers && _this.prev !== _this.current) {
        _this.updateSubscribers.forEach(_this.notifySubscriber);
      }

      if (render && _this.renderSubscribers) {
        _this.renderSubscribers.forEach(_this.notifySubscriber);
      } // Update timestamp


      var _a = getFrameData(),
          delta = _a.delta,
          timestamp = _a.timestamp;

      if (_this.lastUpdated !== timestamp) {
        _this.timeDelta = delta;
        _this.lastUpdated = timestamp;
        sync.postRender(_this.scheduleVelocityCheck);
      }
    };
    /**
     * Notify a subscriber with the latest value.
     *
     * This is an instanced and bound function to prevent generating a new
     * function once per frame.
     *
     * @param subscriber - The subscriber to notify.
     *
     * @internal
     */


    this.notifySubscriber = function (subscriber) {
      subscriber(_this.current);
    };
    /**
     * Schedule a velocity check for the next frame.
     *
     * This is an instanced and bound function to prevent generating a new
     * function once per frame.
     *
     * @internal
     */


    this.scheduleVelocityCheck = function () {
      return sync.postRender(_this.velocityCheck);
    };
    /**
     * Updates `prev` with `current` if the value hasn't been updated this frame.
     * This ensures velocity calculations return `0`.
     *
     * This is an instanced and bound function to prevent generating a new
     * function once per frame.
     *
     * @internal
     */


    this.velocityCheck = function (_a) {
      var timestamp = _a.timestamp;

      if (timestamp !== _this.lastUpdated) {
        _this.prev = _this.current;
      }
    };

    this.set(init, false);
    this.canTrackVelocity = isFloat(this.current);
  }
  /**
   * Subscribes a subscriber function to a subscription list.
   *
   * @param subscriptions - A `Set` of subscribers.
   * @param subscription - A subscriber function.
   */


  MotionValue.prototype.subscribeTo = function (subscriptions, subscription) {
    var _this = this;

    var updateSubscriber = function updateSubscriber() {
      return subscription(_this.current);
    };

    subscriptions.add(updateSubscriber);
    return function () {
      return subscriptions["delete"](updateSubscriber);
    };
  };
  /**
   * Adds a function that will be notified when the `MotionValue` is updated.
   *
   * It returns a function that, when called, will cancel the subscription.
   *
   * When calling `onChange` inside a React component, it should be wrapped with the
   * `useEffect` hook. As it returns an unsubscribe function, this should be returned
   * from the `useEffect` function to ensure you don't add duplicate subscribers..
   *
   * @library
   *
   * ```jsx
   * function MyComponent() {
   *   const x = useMotionValue(0)
   *   const y = useMotionValue(0)
   *   const opacity = useMotionValue(1)
   *
   *   useEffect(() => {
   *     function updateOpacity() {
   *       const maxXY = Math.max(x.get(), y.get())
   *       const newOpacity = transform(maxXY, [0, 100], [1, 0])
   *       opacity.set(newOpacity)
   *     }
   *
   *     const unsubscribeX = x.onChange(updateOpacity)
   *     const unsubscribeY = y.onChange(updateOpacity)
   *
   *     return () => {
   *       unsubscribeX()
   *       unsubscribeY()
   *     }
   *   }, [])
   *
   *   return <Frame x={x} />
   * }
   * ```
   *
   * @motion
   *
   * ```jsx
   * export const MyComponent = () => {
   *   const x = useMotionValue(0)
   *   const y = useMotionValue(0)
   *   const opacity = useMotionValue(1)
   *
   *   useEffect(() => {
   *     function updateOpacity() {
   *       const maxXY = Math.max(x.get(), y.get())
   *       const newOpacity = transform(maxXY, [0, 100], [1, 0])
   *       opacity.set(newOpacity)
   *     }
   *
   *     const unsubscribeX = x.onChange(updateOpacity)
   *     const unsubscribeY = y.onChange(updateOpacity)
   *
   *     return () => {
   *       unsubscribeX()
   *       unsubscribeY()
   *     }
   *   }, [])
   *
   *   return <motion.div style={{ x }} />
   * }
   * ```
   *
   * @internalremarks
   *
   * We could look into a `useOnChange` hook if the above lifecycle management proves confusing.
   *
   * ```jsx
   * useOnChange(x, () => {})
   * ```
   *
   * @param subscriber - A function that receives the latest value.
   * @returns A function that, when called, will cancel this subscription.
   *
   * @public
   */


  MotionValue.prototype.onChange = function (subscription) {
    if (!this.updateSubscribers) this.updateSubscribers = new Set();
    return this.subscribeTo(this.updateSubscribers, subscription);
  };
  /**
   * Adds a function that will be notified when the `MotionValue` requests a render.
   *
   * @param subscriber - A function that's provided the latest value.
   * @returns A function that, when called, will cancel this subscription.
   *
   * @internal
   */


  MotionValue.prototype.onRenderRequest = function (subscription) {
    if (!this.renderSubscribers) this.renderSubscribers = new Set(); // Render immediately

    this.notifySubscriber(subscription);
    return this.subscribeTo(this.renderSubscribers, subscription);
  };
  /**
   * Attaches a passive effect to the `MotionValue`.
   *
   * @internal
   */


  MotionValue.prototype.attach = function (passiveEffect) {
    this.passiveEffect = passiveEffect;
  };
  /**
   * Sets the state of the `MotionValue`.
   *
   * @remarks
   *
   * ```jsx
   * const x = useMotionValue(0)
   * x.set(10)
   * ```
   *
   * @param latest - Latest value to set.
   * @param render - Whether to notify render subscribers. Defaults to `true`
   *
   * @public
   */


  MotionValue.prototype.set = function (v, render) {
    if (render === void 0) {
      render = true;
    }

    if (!render || !this.passiveEffect) {
      this.updateAndNotify(v, render);
    } else {
      this.passiveEffect(v, this.updateAndNotify);
    }
  };
  /**
   * Returns the latest state of `MotionValue`
   *
   * @returns - The latest state of `MotionValue`
   *
   * @public
   */


  MotionValue.prototype.get = function () {
    return this.current;
  };
  /**
   * @public
   */


  MotionValue.prototype.getPrevious = function () {
    return this.prev;
  };
  /**
   * Returns the latest velocity of `MotionValue`
   *
   * @returns - The latest velocity of `MotionValue`. Returns `0` if the state is non-numerical.
   *
   * @public
   */


  MotionValue.prototype.getVelocity = function () {
    // This could be isFloat(this.prev) && isFloat(this.current), but that would be wasteful
    return this.canTrackVelocity ? // These casts could be avoided if parseFloat would be typed better
    velocityPerSecond(parseFloat(this.current) - parseFloat(this.prev), this.timeDelta) : 0;
  };
  /**
   * Registers a new animation to control this `MotionValue`. Only one
   * animation can drive a `MotionValue` at one time.
   *
   * ```jsx
   * value.start()
   * ```
   *
   * @param animation - A function that starts the provided animation
   *
   * @internal
   */


  MotionValue.prototype.start = function (animation) {
    var _this = this;

    this.stop();
    return new Promise(function (resolve) {
      _this.stopAnimation = animation(resolve);
    }).then(function () {
      return _this.clearAnimation();
    });
  };
  /**
   * Stop the currently active animation.
   *
   * @public
   */


  MotionValue.prototype.stop = function () {
    if (this.stopAnimation) this.stopAnimation();
    this.clearAnimation();
  };
  /**
   * Returns `true` if this value is currently animating.
   *
   * @public
   */


  MotionValue.prototype.isAnimating = function () {
    return !!this.stopAnimation;
  };

  MotionValue.prototype.clearAnimation = function () {
    this.stopAnimation = null;
  };
  /**
   * Destroy and clean up subscribers to this `MotionValue`.
   *
   * The `MotionValue` hooks like `useMotionValue` and `useTransform` automatically
   * handle the lifecycle of the returned `MotionValue`, so this method is only necessary if you've manually
   * created a `MotionValue` via the `motionValue` function.
   *
   * @public
   */


  MotionValue.prototype.destroy = function () {
    this.updateSubscribers && this.updateSubscribers.clear();
    this.renderSubscribers && this.renderSubscribers.clear();
    this.stop();
  };

  return MotionValue;
}();
/**
 * @internal
 */


function motionValue(init) {
  return new MotionValue(init);
}
/**
 * Creates a constant value over the lifecycle of a component.
 *
 * Even if `useMemo` is provided an empty array as its final argument, it doesn't offer
 * a guarantee that it won't re-run for performance reasons later on. By using `useConstant`
 * you can ensure that initialisers don't execute twice or more.
 */


function useConstant(init) {
  var ref = useRef(null);

  if (ref.current === null) {
    ref.current = init();
  }

  return ref.current;
}

var isMotionValue = function isMotionValue(value) {
  return value instanceof MotionValue;
}; // Creating a styler factory for the `onUpdate` prop allows all values
// to fire and the `onUpdate` prop will only fire once per frame


var updateStyler = createStylerFactory({
  onRead: function onRead() {
    return null;
  },
  onRender: function onRender(state, _a) {
    var onUpdate = _a.onUpdate;
    return onUpdate(state);
  }
});

var MotionValuesMap =
/** @class */
function () {
  function MotionValuesMap() {
    this.hasMounted = false;
    this.values = new Map();
    this.unsubscribers = new Map();
  }

  MotionValuesMap.prototype.has = function (key) {
    return this.values.has(key);
  };

  MotionValuesMap.prototype.set = function (key, value) {
    this.values.set(key, value);

    if (this.hasMounted) {
      this.bindValueToOutput(key, value);
    }
  };

  MotionValuesMap.prototype.get = function (key, defaultValue) {
    var value = this.values.get(key);

    if (value === undefined && defaultValue !== undefined) {
      value = new MotionValue(defaultValue);
      this.set(key, value);
    }

    return value;
  };

  MotionValuesMap.prototype.forEach = function (callback) {
    return this.values.forEach(callback);
  };

  MotionValuesMap.prototype.bindValueToOutput = function (key, value) {
    var _this = this;

    var onRender = function onRender(v) {
      return _this.output && _this.output(key, v);
    };

    var unsubscribeOnRender = value.onRenderRequest(onRender);

    var onChange = function onChange(v) {
      _this.onUpdate && _this.onUpdate.set(key, v);
    };

    var unsubscribeOnChange = value.onChange(onChange);

    if (this.unsubscribers.has(key)) {
      this.unsubscribers.get(key)();
    }

    this.unsubscribers.set(key, function () {
      unsubscribeOnRender();
      unsubscribeOnChange();
    });
  };

  MotionValuesMap.prototype.setOnUpdate = function (onUpdate) {
    this.onUpdate = undefined;

    if (onUpdate) {
      this.onUpdate = updateStyler({
        onUpdate: onUpdate
      });
    }
  };

  MotionValuesMap.prototype.setTransformTemplate = function (transformTemplate) {
    if (this.transformTemplate !== transformTemplate) {
      this.transformTemplate = transformTemplate;
      this.updateTransformTemplate();
    }
  };

  MotionValuesMap.prototype.getTransformTemplate = function () {
    return this.transformTemplate;
  };

  MotionValuesMap.prototype.updateTransformTemplate = function () {
    if (this.output) {
      this.output("transform", this.transformTemplate);
    }
  };

  MotionValuesMap.prototype.mount = function (output) {
    var _this = this;

    this.hasMounted = true;
    if (output) this.output = output;
    this.values.forEach(function (value, key) {
      return _this.bindValueToOutput(key, value);
    });
    this.updateTransformTemplate();
  };

  MotionValuesMap.prototype.unmount = function () {
    var _this = this;

    this.values.forEach(function (_value, key) {
      var unsubscribe = _this.unsubscribers.get(key);

      unsubscribe && unsubscribe();
    });
  };

  return MotionValuesMap;
}();

var specialMotionValueProps = new Set(["dragOriginX", "dragOriginY"]);

var useMotionValues = function useMotionValues(props) {
  var motionValues = useConstant(function () {
    var map = new MotionValuesMap();
    /**
     * Loop through every prop and add any detected `MotionValue`s. This is SVG-specific
     * code that should be extracted, perhaps considered hollistically with `useMotionStyles`.
     *
     * <motion.circle cx={motionValue(0)} />
     */

    for (var key in props) {
      if (isMotionValue(props[key]) && !specialMotionValueProps.has(key)) {
        map.set(key, props[key]);
      }
    }

    return map;
  });
  motionValues.setOnUpdate(props.onUpdate);
  motionValues.setTransformTemplate(props.transformTemplate);
  return motionValues;
};

var createValueResolver = function createValueResolver(resolver) {
  return function (values) {
    var resolvedValues = {};
    values.forEach(function (value, key) {
      return resolvedValues[key] = resolver(value);
    });
    return resolvedValues;
  };
};

var resolveCurrent = createValueResolver(function (value) {
  return value.get();
});
var transformOriginProps = new Set(["originX", "originY", "originZ"]);

var isTransformOriginProp = function isTransformOriginProp(key) {
  return transformOriginProps.has(key);
};

var buildStyleAttr = function buildStyleAttr(values, styleProp, isStatic) {
  var motionValueStyles = resolveCurrent(values);
  var transformTemplate = values.getTransformTemplate();

  if (transformTemplate) {
    // If `transform` has been manually set as a string, pass that through the template
    // otherwise pass it forward to Stylefire's style property builder
    motionValueStyles.transform = styleProp.transform ? transformTemplate({}, styleProp.transform) : transformTemplate;
  }

  return buildStyleProperty(__assign(__assign({}, styleProp), motionValueStyles), !isStatic);
};

var useMotionStyles = function useMotionStyles(values, styleProp, isStatic, transformValues) {
  if (styleProp === void 0) {
    styleProp = {};
  }

  var style = {};
  var prevMotionStyles = useRef({}).current;

  for (var key in styleProp) {
    var thisStyle = styleProp[key];

    if (isMotionValue(thisStyle)) {
      // If this is a motion value, add it to our MotionValuesMap
      values.set(key, thisStyle);
    } else if (!isStatic && (isTransformProp(key) || isTransformOriginProp(key))) {
      // Or if it's a transform prop, create a motion value (or update an existing one)
      // to ensure Stylefire can reconcile all the transform values together.
      // A further iteration on this would be to create a single styler per component that gets
      // used in the DOM renderer's buildStyleAttr *and* animations, then we would only
      // have to convert animating values to `MotionValues` (we could probably remove this entire function).
      // The only architectural consideration is to allow Stylefire to have elements mounted after
      // a styler is created.
      if (!values.has(key)) {
        // If it doesn't exist as a motion value, create it
        values.set(key, motionValue(thisStyle));
      } else {
        // Otherwise only update it if it's changed from a previous render
        if (thisStyle !== prevMotionStyles[key]) {
          var value = values.get(key);
          value.set(thisStyle);
        }
      }

      prevMotionStyles[key] = thisStyle;
    } else {
      style[key] = thisStyle;
    }
  }

  return transformValues ? transformValues(style) : style;
};

var isKeyframesTarget = function isKeyframesTarget(v) {
  return Array.isArray(v);
};

var isCustomValue = function isCustomValue(v) {
  return Boolean(v && typeof v === "object" && v.mix && v.toValue);
};

var resolveFinalValueInKeyframes = function resolveFinalValueInKeyframes(v) {
  // TODO maybe throw if v.length - 1 is placeholder token?
  return isKeyframesTarget(v) ? v[v.length - 1] || 0 : v;
};

var auto = {
  test: function test(v) {
    return v === "auto";
  },
  parse: function parse(v) {
    return v;
  }
};
var dimensionTypes = [number, px, percent, degrees, vw, vh, auto];

var valueTypes = __spreadArrays(dimensionTypes, [color, complex]);

var testValueType = function testValueType(v) {
  return function (type) {
    return type.test(v);
  };
};

var getDimensionValueType = function getDimensionValueType(v) {
  return dimensionTypes.find(testValueType(v));
};

var getValueType = function getValueType(v) {
  return valueTypes.find(testValueType(v));
};

var underDampedSpring = function underDampedSpring() {
  return {
    type: "spring",
    stiffness: 500,
    damping: 25,
    restDelta: 0.5,
    restSpeed: 10
  };
};

var overDampedSpring = function overDampedSpring(to) {
  return {
    type: "spring",
    stiffness: 700,
    damping: to === 0 ? 100 : 35
  };
};

var linearTween = function linearTween() {
  return {
    ease: "linear",
    duration: 0.3
  };
};

var keyframes = function keyframes(values) {
  return {
    type: "keyframes",
    duration: 0.8,
    values: values
  };
};

var defaultTransitions = {
  x: underDampedSpring,
  y: underDampedSpring,
  z: underDampedSpring,
  rotate: underDampedSpring,
  rotateX: underDampedSpring,
  rotateY: underDampedSpring,
  rotateZ: underDampedSpring,
  scaleX: overDampedSpring,
  scaleY: overDampedSpring,
  scale: overDampedSpring,
  opacity: linearTween,
  backgroundColor: linearTween,
  color: linearTween,
  "default": overDampedSpring
};

var getDefaultTransition = function getDefaultTransition(valueKey, to) {
  var transitionFactory;

  if (isKeyframesTarget(to)) {
    transitionFactory = keyframes;
  } else {
    transitionFactory = defaultTransitions[valueKey] || defaultTransitions["default"];
  }

  return __assign({
    to: to
  }, transitionFactory(to));
};
/**
 * A Popmotion action that accepts a single `to` prop. When it starts, it immediately
 * updates with `to` and then completes. By using this we can compose instant transitions
 * in with the same logic that applies `delay` or returns a `Promise` etc.
 *
 * Accepting `duration` is a little bit of a hack that simply defers the completetion of
 * the animation until after the duration finishes. This is for situations when you're **only**
 * animating non-animatable values and then setting something on `transitionEnd`. Really
 * you want this to fire after the "animation" finishes, rather than instantly.
 *
 * ```
 * animate={{
 *   display: 'block',
 *   transitionEnd: { display: 'none' }
 * }}
 * ```
 */


var just = function just(_a) {
  var to = _a.to,
      duration = _a.duration;
  return action(function (_a) {
    var update = _a.update,
        complete = _a.complete;
    update(to);
    duration ? delay(duration).start({
      complete: complete
    }) : complete();
  });
};

var easingDefinitionToFunction = function easingDefinitionToFunction(definition) {
  if (Array.isArray(definition)) {
    // If cubic bezier definition, create bezier curve
    invariant(definition.length === 4, "Cubic bezier arrays must contain four numerical values.");
    var x1 = definition[0],
        y1 = definition[1],
        x2 = definition[2],
        y2 = definition[3];
    return cubicBezier(x1, y1, x2, y2);
  } else if (typeof definition === "string") {
    // Else lookup from table
    invariant(easingLookup[definition] !== undefined, "Invalid easing type '" + definition + "'");
    return easingLookup[definition];
  }

  return definition;
};

var isEasingArray = function isEasingArray(ease) {
  return Array.isArray(ease) && typeof ease[0] !== "number";
};

var isDurationAnimation = function isDurationAnimation(v) {
  return v.hasOwnProperty("duration") || v.hasOwnProperty("repeatDelay");
};
/**
 * Check if a value is animatable. Examples:
 *
 * ✅: 100, "100px", "#fff"
 * ❌: "block", "url(2.jpg)"
 * @param value
 *
 * @internal
 */


var isAnimatable = function isAnimatable(key, value) {
  // If the list of keys tat might be non-animatable grows, replace with Set
  if (key === "zIndex") return false; // If it's a number or a keyframes array, we can animate it. We might at some point
  // need to do a deep isAnimatable check of keyframes, or let Popmotion handle this,
  // but for now lets leave it like this for performance reasons

  if (typeof value === "number" || Array.isArray(value)) return true;

  if (typeof value === "string" && // It's animatable if we have a string
  complex.test(value) && // And it contains numbers and/or colors
  !value.startsWith("url(") // Unless it starts with "url("
  ) {
      return true;
    }

  return false;
};
/**
 * Converts seconds to milliseconds
 *
 * @param seconds - Time in seconds.
 * @return milliseconds - Converted time in milliseconds.
 */


var secondsToMilliseconds = function secondsToMilliseconds(seconds) {
  return seconds * 1000;
};

var transitions = {
  tween: tween,
  spring: spring,
  keyframes: keyframes$1,
  inertia: inertia,
  just: just
};
var transitionOptionParser = {
  tween: function tween(opts) {
    if (opts.ease) {
      var ease = isEasingArray(opts.ease) ? opts.ease[0] : opts.ease;
      opts.ease = easingDefinitionToFunction(ease);
    }

    return opts;
  },
  keyframes: function keyframes(_a) {
    var from = _a.from,
        to = _a.to,
        velocity = _a.velocity,
        opts = __rest(_a, ["from", "to", "velocity"]);

    if (opts.values && opts.values[0] === null) {
      var values = __spreadArrays(opts.values);

      values[0] = from;
      opts.values = values;
    }

    if (opts.ease) {
      opts.easings = isEasingArray(opts.ease) ? opts.ease.map(easingDefinitionToFunction) : easingDefinitionToFunction(opts.ease);
    }

    opts.ease = linear;
    return opts;
  }
};

var isTransitionDefined = function isTransitionDefined(_a) {
  var when = _a.when,
      delay = _a.delay,
      delayChildren = _a.delayChildren,
      staggerChildren = _a.staggerChildren,
      staggerDirection = _a.staggerDirection,
      transition = __rest(_a, ["when", "delay", "delayChildren", "staggerChildren", "staggerDirection"]);

  return Object.keys(transition).length;
};

var getTransitionDefinition = function getTransitionDefinition(key, to, transitionDefinition) {
  var delay = transitionDefinition ? transitionDefinition.delay : 0; // If no object, return default transition
  // A better way to handle this would be to deconstruct out all the shared Orchestration props
  // and see if there's any props remaining

  if (transitionDefinition === undefined || !isTransitionDefined(transitionDefinition)) {
    return __assign({
      delay: delay
    }, getDefaultTransition(key, to));
  }

  var valueTransitionDefinition = transitionDefinition[key] || transitionDefinition["default"] || transitionDefinition;

  if (valueTransitionDefinition.type === false) {
    return {
      delay: valueTransitionDefinition.hasOwnProperty("delay") ? valueTransitionDefinition.delay : delay,
      to: isKeyframesTarget(to) ? to[to.length - 1] : to,
      type: "just"
    };
  } else if (isKeyframesTarget(to)) {
    return __assign(__assign({
      values: to,
      duration: 0.8,
      delay: delay,
      ease: "linear"
    }, valueTransitionDefinition), {
      // This animation must be keyframes if we're animating through an array
      type: "keyframes"
    });
  } else {
    return __assign({
      type: "tween",
      to: to,
      delay: delay
    }, valueTransitionDefinition);
  }
};

var preprocessOptions = function preprocessOptions(type, opts) {
  return transitionOptionParser[type] ? transitionOptionParser[type](opts) : opts;
};

var getAnimation = function getAnimation(key, value, target, transition) {
  var origin = value.get();
  var isOriginAnimatable = isAnimatable(key, origin);
  var isTargetAnimatable = isAnimatable(key, target); // TODO we could probably improve this check to ensure both values are of the same type -
  // for instance 100 to #fff. This might live better in Popmotion.

  warning(isOriginAnimatable === isTargetAnimatable, "You are trying to animate " + key + " from \"" + origin + "\" to \"" + target + "\". " + origin + " is not an animatable value - to enable this animation set " + origin + " to a value animatable to " + target + " via the `style` property."); // Parse the `transition` prop and return options for the Popmotion animation

  var _a = getTransitionDefinition(key, target, transition),
      _b = _a.type,
      type = _b === void 0 ? "tween" : _b,
      transitionDefinition = __rest(_a, ["type"]); // If this is an animatable pair of values, return an animation, otherwise use `just`


  var actionFactory = isOriginAnimatable && isTargetAnimatable ? transitions[type] : just;
  var opts = preprocessOptions(type, __assign({
    from: origin,
    velocity: value.getVelocity()
  }, transitionDefinition)); // Convert duration from Framer Motion's seconds into Popmotion's milliseconds

  if (isDurationAnimation(opts)) {
    if (opts.duration) {
      opts.duration = secondsToMilliseconds(opts.duration);
    }

    if (opts.repeatDelay) {
      opts.repeatDelay = secondsToMilliseconds(opts.repeatDelay);
    }
  }

  return [actionFactory, opts];
};
/**
 * Start animation on a value. This function completely encapsulates Popmotion-specific logic.
 *
 * @internal
 */


function startAnimation(key, value, target, _a) {
  if (_a === void 0) {
    _a = {};
  }

  var _b = _a.delay,
      delay$1 = _b === void 0 ? 0 : _b,
      transition = __rest(_a, ["delay"]);

  return value.start(function (complete) {
    var activeAnimation;

    var _a = getAnimation(key, value, target, transition),
        animationFactory = _a[0],
        _b = _a[1],
        valueDelay = _b.delay,
        options = __rest(_b, ["delay"]);

    if (valueDelay !== undefined) {
      delay$1 = valueDelay;
    }

    var animate = function animate() {
      var animation = animationFactory(options); // Bind animation opts to animation

      activeAnimation = animation.start({
        update: function update(v) {
          return value.set(v);
        },
        complete: complete
      });
    }; // If we're delaying this animation, only resolve it **after** the delay to
    // ensure the value's resolve velocity is up-to-date.


    if (delay$1) {
      activeAnimation = delay(secondsToMilliseconds(delay$1)).start({
        complete: animate
      });
    } else {
      animate();
    }

    return function () {
      if (activeAnimation) activeAnimation.stop();
    };
  });
}
/**
 * Check if value is a numerical string, ie a string that is purely a number eg "100" or "-100.1"
 */


var isNumericalString = function isNumericalString(v) {
  return /^\-?\d*\.?\d+$/.test(v);
};
/**
 * Get the current value of every `MotionValue`
 * @param values -
 */


var getCurrent = function getCurrent(values) {
  var current = {};
  values.forEach(function (value, key) {
    return current[key] = value.get();
  });
  return current;
};
/**
 * Get the current velocity of every `MotionValue`
 * @param values -
 */


var getVelocity = function getVelocity(values) {
  var velocity = {};
  values.forEach(function (value, key) {
    return velocity[key] = value.getVelocity();
  });
  return velocity;
};
/**
 * Check if value is a function that returns a `Target`. A generic typeof === 'function'
 * check, just helps with typing.
 * @param p -
 */


var isTargetResolver = function isTargetResolver(p) {
  return typeof p === "function";
};
/**
 * Check if value is a list of variant labels
 * @param v -
 */


var isVariantLabels = function isVariantLabels(v) {
  return Array.isArray(v);
};
/**
 * Control animations for a single component
 *
 * @internal
 */


var ValueAnimationControls =
/** @class */
function () {
  function ValueAnimationControls(_a) {
    var _this = this;

    var values = _a.values,
        readValueFromSource = _a.readValueFromSource,
        makeTargetAnimatable = _a.makeTargetAnimatable;
    /**
     * A reference to the component's latest props. We could probably ditch this in
     * favour to a reference to the `custom` prop now we don't send all props through
     * to target resolvers.
     */

    this.props = {};
    /**
     * The component's variants, as provided by `variants`
     */

    this.variants = {};
    /**
     * A set of values that we animate back to when a value is cleared of all overrides.
     */

    this.baseTarget = {};
    /**
     * A series of target overrides that we can animate to/from when overrides are set/cleared.
     */

    this.overrides = [];
    /**
     * A series of target overrides as they were originally resolved.
     */

    this.resolvedOverrides = [];
    /**
     * A Set of currently active override indexes
     */

    this.activeOverrides = new Set();
    /**
     * A Set of value keys that are currently animating.
     */

    this.isAnimating = new Set();
    /**
     * Check if the associated `MotionValueMap` has a key with the provided string.
     * Pre-bound to the class so we can provide directly to the `filter` in `checkForNewValues`.
     */

    this.hasValue = function (key) {
      return !_this.values.has(key);
    };

    this.values = values;
    this.readValueFromSource = readValueFromSource;
    this.makeTargetAnimatable = makeTargetAnimatable;
    this.values.forEach(function (value, key) {
      return _this.baseTarget[key] = value.get();
    });
  }
  /**
   * Set the reference to the component's props.
   * @param props -
   */


  ValueAnimationControls.prototype.setProps = function (props) {
    this.props = props;
  };
  /**
   * Set the reference to the component's variants
   * @param variants -
   */


  ValueAnimationControls.prototype.setVariants = function (variants) {
    if (variants) this.variants = variants;
  };
  /**
   * Set the component's default transition
   * @param transition -
   */


  ValueAnimationControls.prototype.setDefaultTransition = function (transition) {
    if (transition) this.defaultTransition = transition;
  };
  /**
   * Set motion values without animation.
   *
   * @param definition -
   * @param isActive -
   */


  ValueAnimationControls.prototype.setValues = function (definition, _a) {
    var _this = this;

    var _b = _a === void 0 ? {} : _a,
        _c = _b.isActive,
        isActive = _c === void 0 ? new Set() : _c,
        priority = _b.priority;

    var _d = this.resolveVariant(definition),
        target = _d.target,
        transitionEnd = _d.transitionEnd;

    target = this.transformValues(__assign(__assign({}, target), transitionEnd));
    return Object.keys(target).forEach(function (key) {
      if (isActive.has(key)) return;
      isActive.add(key);

      if (target) {
        var targetValue = resolveFinalValueInKeyframes(target[key]);

        if (_this.values.has(key)) {
          var value = _this.values.get(key);

          value && value.set(targetValue);
        } else {
          _this.values.set(key, motionValue(targetValue));
        }

        if (!priority) _this.baseTarget[key] = targetValue;
      }
    });
  };
  /**
   * Allows `transformValues` to be set by a component that allows us to
   * transform the values in a given `Target`. This allows Framer Library
   * to extend Framer Motion to animate `Color` variables etc. Currently we have
   * to manually support these extended types here in Framer Motion.
   *
   * @param values -
   */


  ValueAnimationControls.prototype.transformValues = function (values) {
    var transformValues = this.props.transformValues;
    return transformValues ? transformValues(values) : values;
  };
  /**
   * Check a `Target` for new values we haven't animated yet, and add them
   * to the `MotionValueMap`.
   *
   * Currently there's functionality here that is DOM-specific, we should allow
   * this functionality to be injected by the factory that creates DOM-specific
   * components.
   *
   * @param target -
   */


  ValueAnimationControls.prototype.checkForNewValues = function (target) {
    var newValueKeys = Object.keys(target).filter(this.hasValue);
    var numNewValues = newValueKeys.length;
    if (!numNewValues) return;

    for (var i = 0; i < numNewValues; i++) {
      var key = newValueKeys[i];
      var targetValue = target[key];
      var value = null; // If this is a keyframes value, we can attempt to use the first value in the
      // array as that's going to be the first value of the animation anyway

      if (Array.isArray(targetValue)) {
        value = targetValue[0];
      } // If it isn't a keyframes or the first keyframes value was set as `null`, read the
      // value from the DOM. It might be worth investigating whether to check props (for SVG)
      // or props.style (for HTML) if the value exists there before attempting to read.


      if (value === null) {
        value = this.readValueFromSource(key);
        invariant(value !== null, "No initial value for \"" + key + "\" can be inferred. Ensure an initial value for \"" + key + "\" is defined on the component.");
      }

      if (typeof value === "string" && isNumericalString(value)) {
        // If this is a number read as a string, ie "0" or "200", convert it to a number
        value = parseFloat(value);
      } else if (!getValueType(value) && complex.test(targetValue)) {
        // If value is not recognised as animatable, ie "none", create an animatable version origin based on the target
        value = complex.getAnimatableNone(targetValue);
      }

      this.values.set(key, motionValue(value));
      this.baseTarget[key] = value;
    }
  };
  /**
   * Resolve a variant from its label or resolver into an actual `Target` we can animate to.
   * @param variant -
   */


  ValueAnimationControls.prototype.resolveVariant = function (variant) {
    if (!variant) {
      return {
        target: undefined,
        transition: undefined,
        transitionEnd: undefined
      };
    }

    if (isTargetResolver(variant)) {
      // resolve current and velocity
      variant = variant(this.props.custom, getCurrent(this.values), getVelocity(this.values));
    }

    var _a = variant.transition,
        transition = _a === void 0 ? this.defaultTransition : _a,
        transitionEnd = variant.transitionEnd,
        target = __rest(variant, ["transition", "transitionEnd"]);

    return {
      transition: transition,
      transitionEnd: transitionEnd,
      target: target
    };
  };
  /**
   * Get the highest active override priority index
   */


  ValueAnimationControls.prototype.getHighestPriority = function () {
    if (!this.activeOverrides.size) return 0;
    return Math.max.apply(Math, Array.from(this.activeOverrides));
  };
  /**
   * Set an override. We add this layer of indirection so if, for instance, a tap gesture
   * starts and overrides a hover gesture, when we clear the tap gesture and fallback to the
   * hover gesture, if that hover gesture has changed in the meantime we can go to that rather
   * than the one that was resolved when the hover gesture animation started.
   *
   * @param definition -
   * @param overrideIndex -
   */


  ValueAnimationControls.prototype.setOverride = function (definition, overrideIndex) {
    this.overrides[overrideIndex] = definition;

    if (this.children) {
      this.children.forEach(function (child) {
        return child.setOverride(definition, overrideIndex);
      });
    }
  };
  /**
   * Start an override animation.
   * @param overrideIndex -
   */


  ValueAnimationControls.prototype.startOverride = function (overrideIndex) {
    var override = this.overrides[overrideIndex];

    if (override) {
      return this.start(override, {
        priority: overrideIndex
      });
    }
  };
  /**
   * Clear an override. We check every value we animated to in this override to see if
   * its present on any lower-priority overrides. If not, we animate it back to its base target.
   * @param overrideIndex -
   */


  ValueAnimationControls.prototype.clearOverride = function (overrideIndex) {
    var _this = this;

    if (this.children) {
      this.children.forEach(function (child) {
        return child.clearOverride(overrideIndex);
      });
    }

    var override = this.overrides[overrideIndex];
    if (!override) return;
    this.activeOverrides["delete"](overrideIndex);
    var highest = this.getHighestPriority();
    this.resetIsAnimating();

    if (highest) {
      var highestOverride = this.overrides[highest];
      highestOverride && this.startOverride(highest);
    } // Figure out which remaining values were affected by the override and animate those


    var overrideTarget = this.resolvedOverrides[overrideIndex];
    if (!overrideTarget) return;
    var remainingValues = {};

    for (var key in this.baseTarget) {
      if (overrideTarget[key] !== undefined) {
        remainingValues[key] = this.baseTarget[key];
      }
    }

    this.onStart();
    this.animate(remainingValues).then(function () {
      return _this.onComplete();
    });
  };
  /**
   * Apply a target/variant without any animation
   */


  ValueAnimationControls.prototype.apply = function (definition) {
    if (Array.isArray(definition)) {
      return this.applyVariantLabels(definition);
    } else if (typeof definition === "string") {
      return this.applyVariantLabels([definition]);
    } else {
      this.setValues(definition);
    }
  };
  /**
   * Apply variant labels without animation
   */


  ValueAnimationControls.prototype.applyVariantLabels = function (variantLabelList) {
    var _this = this;

    var isActive = new Set();

    var reversedList = __spreadArrays(variantLabelList).reverse();

    reversedList.forEach(function (key) {
      var _a = _this.resolveVariant(_this.variants[key]),
          target = _a.target,
          transitionEnd = _a.transitionEnd;

      if (transitionEnd) {
        _this.setValues(transitionEnd, {
          isActive: isActive
        });
      }

      if (target) {
        _this.setValues(target, {
          isActive: isActive
        });
      }

      if (_this.children && _this.children.size) {
        _this.children.forEach(function (child) {
          return child.applyVariantLabels(variantLabelList);
        });
      }
    });
  };

  ValueAnimationControls.prototype.start = function (definition, opts) {
    var _this = this;

    if (opts === void 0) {
      opts = {};
    }

    if (opts.priority) {
      this.activeOverrides.add(opts.priority);
    }

    this.resetIsAnimating(opts.priority);
    var animation;

    if (isVariantLabels(definition)) {
      animation = this.animateVariantLabels(definition, opts);
    } else if (typeof definition === "string") {
      animation = this.animateVariant(definition, opts);
    } else {
      animation = this.animate(definition, opts);
    }

    this.onStart();
    return animation.then(function () {
      return _this.onComplete();
    });
  };

  ValueAnimationControls.prototype.animate = function (animationDefinition, _a) {
    var _this = this;

    var _b = _a === void 0 ? {} : _a,
        _c = _b.delay,
        delay = _c === void 0 ? 0 : _c,
        _d = _b.priority,
        priority = _d === void 0 ? 0 : _d,
        transitionOverride = _b.transitionOverride;

    var _e = this.resolveVariant(animationDefinition),
        target = _e.target,
        transition = _e.transition,
        transitionEnd = _e.transitionEnd;

    if (transitionOverride) {
      transition = transitionOverride;
    }

    if (!target) return Promise.resolve();
    target = this.transformValues(target);

    if (transitionEnd) {
      transitionEnd = this.transformValues(transitionEnd);
    }

    this.checkForNewValues(target);

    if (this.makeTargetAnimatable) {
      var animatable = this.makeTargetAnimatable(target, transitionEnd);
      target = animatable.target;
      transitionEnd = animatable.transitionEnd;
    }

    if (priority) {
      this.resolvedOverrides[priority] = target;
    }

    this.checkForNewValues(target);
    var animations = [];

    for (var key in target) {
      var value = this.values.get(key);
      if (!value || !target || target[key] === undefined) continue;
      var valueTarget = target[key];

      if (!priority) {
        this.baseTarget[key] = resolveFinalValueInKeyframes(valueTarget);
      }

      if (this.isAnimating.has(key)) continue;
      this.isAnimating.add(key);
      animations.push(startAnimation(key, value, valueTarget, __assign({
        delay: delay
      }, transition)));
    }

    var allAnimations = Promise.all(animations);
    return transitionEnd ? allAnimations.then(function () {
      _this.setValues(transitionEnd, {
        priority: priority
      });
    }) : allAnimations;
  };

  ValueAnimationControls.prototype.animateVariantLabels = function (variantLabels, opts) {
    var _this = this;

    var animations = __spreadArrays(variantLabels).reverse().map(function (label) {
      return _this.animateVariant(label, opts);
    });

    return Promise.all(animations);
  };

  ValueAnimationControls.prototype.animateVariant = function (variantLabel, opts) {
    var _this = this;

    var when = false;
    var delayChildren = 0;
    var staggerChildren = 0;
    var staggerDirection = 1;
    var priority = opts && opts.priority || 0;
    var variant = this.variants[variantLabel];
    var getAnimations = variant ? function () {
      return _this.animate(variant, opts);
    } : function () {
      return Promise.resolve();
    };
    var getChildrenAnimations = this.children ? function () {
      return _this.animateChildren(variantLabel, delayChildren, staggerChildren, staggerDirection, priority);
    } : function () {
      return Promise.resolve();
    };

    if (variant && this.children) {
      var transition = this.resolveVariant(variant).transition;

      if (transition) {
        when = transition.when || when;
        delayChildren = transition.delayChildren || delayChildren;
        staggerChildren = transition.staggerChildren || staggerChildren;
        staggerDirection = transition.staggerDirection || staggerDirection;
      }
    }

    if (when) {
      var _a = when === "beforeChildren" ? [getAnimations, getChildrenAnimations] : [getChildrenAnimations, getAnimations],
          first = _a[0],
          last = _a[1];

      return first().then(last);
    } else {
      return Promise.all([getAnimations(), getChildrenAnimations()]);
    }
  };

  ValueAnimationControls.prototype.animateChildren = function (variantLabel, delayChildren, staggerChildren, staggerDirection, priority) {
    if (delayChildren === void 0) {
      delayChildren = 0;
    }

    if (staggerChildren === void 0) {
      staggerChildren = 0;
    }

    if (staggerDirection === void 0) {
      staggerDirection = 1;
    }

    if (priority === void 0) {
      priority = 0;
    }

    if (!this.children) {
      return Promise.resolve();
    }

    var animations = [];
    var maxStaggerDuration = (this.children.size - 1) * staggerChildren;
    var generateStaggerDuration = staggerDirection === 1 ? function (i) {
      return i * staggerChildren;
    } : function (i) {
      return maxStaggerDuration - i * staggerChildren;
    };
    Array.from(this.children).forEach(function (childControls, i) {
      var animation = childControls.animateVariant(variantLabel, {
        priority: priority,
        delay: delayChildren + generateStaggerDuration(i)
      });
      animations.push(animation);
    });
    return Promise.all(animations);
  };

  ValueAnimationControls.prototype.onStart = function () {
    var onAnimationStart = this.props.onAnimationStart;
    onAnimationStart && onAnimationStart();
  };

  ValueAnimationControls.prototype.onComplete = function () {
    var onAnimationComplete = this.props.onAnimationComplete;
    onAnimationComplete && onAnimationComplete();
  };

  ValueAnimationControls.prototype.checkOverrideIsAnimating = function (priority) {
    var numOverrides = this.overrides.length;

    for (var i = priority + 1; i < numOverrides; i++) {
      var resolvedOverride = this.resolvedOverrides[i];

      if (resolvedOverride) {
        for (var key in resolvedOverride) {
          this.isAnimating.add(key);
        }
      }
    }
  };

  ValueAnimationControls.prototype.resetIsAnimating = function (priority) {
    if (priority === void 0) {
      priority = 0;
    }

    this.isAnimating.clear(); // If this isn't the highest priority gesture, block the animation
    // of anything that's currently being animated

    if (priority < this.getHighestPriority()) {
      this.checkOverrideIsAnimating(priority);
    }

    if (this.children) {
      this.children.forEach(function (child) {
        return child.resetIsAnimating(priority);
      });
    }
  };

  ValueAnimationControls.prototype.stop = function () {
    this.values.forEach(function (value) {
      return value.stop();
    });
  };
  /**
   * Add the controls of a child component.
   * @param controls -
   */


  ValueAnimationControls.prototype.addChild = function (controls) {
    if (!this.children) {
      this.children = new Set();
    }

    this.children.add(controls); // We set child overrides when `setOverride` is called, but also have to do it here
    // as the first time `setOverride` is called all the children might not have been added yet.

    this.overrides.forEach(function (override, i) {
      override && controls.setOverride(override, i);
    });
  };

  ValueAnimationControls.prototype.removeChild = function (controls) {
    if (!this.children) {
      return;
    }

    this.children["delete"](controls);
  };

  ValueAnimationControls.prototype.resetChildren = function () {
    if (this.children) this.children.clear();
  };

  return ValueAnimationControls;
}();
/**
 * Use callback either only on the initial render or on all renders. In concurrent mode
 * the "initial" render might run multiple times
 *
 * @param callback - Callback to run
 * @param isInitialOnly - Set to `true` to only run on initial render, or `false` for all renders. Defaults to `false`.
 *
 * @public
 */


function useInitialOrEveryRender(callback, isInitialOnly) {
  if (isInitialOnly === void 0) {
    isInitialOnly = false;
  }

  var isInitialRender = useRef(true);

  if (!isInitialOnly || isInitialOnly && isInitialRender.current) {
    callback();
  }

  isInitialRender.current = false;
}
/**
 * Control animations on one or more components.
 *
 * @public
 */


var AnimationControls =
/** @class */
function () {
  function AnimationControls() {
    /**
     * Track whether the host component has mounted.
     *
     * @internal
     */
    this.hasMounted = false;
    /**
     * Pending animations that are started before a component is mounted.
     *
     * @internal
     */

    this.pendingAnimations = [];
    /**
     * A collection of linked component animation controls.
     *
     * @internal
     */

    this.componentControls = new Set();
  }
  /**
   * Set variants on this and all child components.
   *
   * @param variants - The variants to set
   *
   * @internal
   */


  AnimationControls.prototype.setVariants = function (variants) {
    this.variants = variants;
    this.componentControls.forEach(function (controls) {
      return controls.setVariants(variants);
    });
  };
  /**
   * Set a default transition on this and all child components
   *
   * @param transition - The default transition to set
   *
   * @internal
   */


  AnimationControls.prototype.setDefaultTransition = function (transition) {
    this.defaultTransition = transition;
    this.componentControls.forEach(function (controls) {
      return controls.setDefaultTransition(transition);
    });
  };
  /**
   * Subscribes a component's animation controls to this.
   *
   * @param controls - The controls to subscribe
   * @returns An unsubscribe function.
   *
   * @internal
   */


  AnimationControls.prototype.subscribe = function (controls) {
    var _this = this;

    this.componentControls.add(controls);
    if (this.variants) controls.setVariants(this.variants);
    if (this.defaultTransition) controls.setDefaultTransition(this.defaultTransition);
    return function () {
      return _this.componentControls["delete"](controls);
    };
  };
  /**
   * Starts an animation on all linked components.
   *
   * @remarks
   *
   * ```jsx
   * controls.start("variantLabel")
   * controls.start({
   *   x: 0,
   *   transition: { duration: 1 }
   * })
   * ```
   *
   * @param definition - Properties or variant label to animate to
   * @param transition - Optional `transtion` to apply to a variant
   * @returns - A `Promise` that resolves when all animations have completed.
   *
   * @public
   */


  AnimationControls.prototype.start = function (definition, transitionOverride) {
    var _this = this;

    if (this.hasMounted) {
      var animations_1 = [];
      this.componentControls.forEach(function (controls) {
        var animation = controls.start(definition, {
          transitionOverride: transitionOverride
        });
        animations_1.push(animation);
      });
      return Promise.all(animations_1);
    } else {
      return new Promise(function (resolve) {
        _this.pendingAnimations.push({
          animation: [definition, transitionOverride],
          resolve: resolve
        });
      });
    }
  };
  /**
   * Instantly set to a set of properties or a variant.
   *
   * ```jsx
   * // With properties
   * controls.set({ opacity: 0 })
   *
   * // With variants
   * controls.set("hidden")
   * ```
   *
   * @internalremarks
   * We could perform a similar trick to `.start` where this can be called before mount
   * and we maintain a list of of pending actions that get applied on mount. But the
   * expectation of `set` is that it happens synchronously and this would be difficult
   * to do before any children have even attached themselves. It's also poor practise
   * and we should discourage render-synchronous `.start` calls rather than lean into this.
   *
   * @public
   */


  AnimationControls.prototype.set = function (definition) {
    invariant(this.hasMounted, "controls.set() should only be called after a component has mounted. Consider calling within a useEffect hook.");
    return this.componentControls.forEach(function (controls) {
      return controls.apply(definition);
    });
  };
  /**
   * Stops animations on all linked components.
   *
   * ```jsx
   * controls.stop()
   * ```
   *
   * @public
   */


  AnimationControls.prototype.stop = function () {
    this.componentControls.forEach(function (controls) {
      return controls.stop();
    });
  };
  /**
   * Initialises the animation controls.
   *
   * @internal
   */


  AnimationControls.prototype.mount = function () {
    var _this = this;

    this.hasMounted = true;
    this.pendingAnimations.forEach(function (_a) {
      var animation = _a.animation,
          resolve = _a.resolve;
      return _this.start.apply(_this, animation).then(resolve);
    });
  };
  /**
   * Stops all child animations when the host component unmounts.
   *
   * @internal
   */


  AnimationControls.prototype.unmount = function () {
    this.hasMounted = false;
    this.stop();
  };

  return AnimationControls;
}();
/**
 * @internal
 */


var animationControls = function animationControls() {
  return new AnimationControls();
};

var PresenceContext = createContext(null);
/**
 * Creates a `MotionValue` to track the state and velocity of a value.
 *
 * Usually, these are created automatically. For advanced use-cases, like use with `useTransform`, you can create `MotionValue`s externally and pass them into the animated component via the `style` prop.
 *
 * @library
 *
 * ```jsx
 * export function MyComponent() {
 *   const scale = useMotionValue(1)
 *
 *   return <Frame scale={scale} />
 * }
 * ```
 *
 * @motion
 *
 * ```jsx
 * export const MyComponent = () => {
 *   const scale = useMotionValue(1)
 *
 *   return <motion.div style={{ scale }} />
 * }
 * ```
 *
 * @param initial - The initial state.
 *
 * @public
 */

function useMotionValue(initial) {
  return useConstant(function () {
    return motionValue(initial);
  });
}
/**
 * If the provided value is a MotionValue, this returns the actual value, otherwise just the value itself
 *
 * TODO: Remove and move to library
 *
 * @internal
 */


function resolveMotionValue(value) {
  var unwrappedValue = value instanceof MotionValue ? value.get() : value;
  return isCustomValue(unwrappedValue) ? unwrappedValue.toValue() : unwrappedValue;
}

var clampProgress = clamp(0, 1);

function snapshotLayout(element, transformPagePoint) {
  var _a = element.getBoundingBox(),
      top = _a.top,
      left = _a.left,
      right = _a.right,
      bottom = _a.bottom;

  var topLeft = transformPagePoint({
    x: left,
    y: top
  });
  var bottomRight = transformPagePoint({
    x: right,
    y: bottom
  });
  return {
    x: {
      min: topLeft.x,
      max: bottomRight.x
    },
    y: {
      min: topLeft.y,
      max: bottomRight.y
    }
  };
}

function snapshotStyle(element, valueHandlers) {
  var computedStyle = element.getComputedStyle();
  var style = {};

  for (var key in valueHandlers) {
    var handler = valueHandlers[key];

    if (handler.read === false) {
      continue;
    } else if (handler.read) {
      style[key] = handler.read(computedStyle[key]);
    } else {
      style[key] = computedStyle[key];
    }
  }

  warning(computedStyle.display !== "inline", "Magic components can't be display: inline, as inline elements don't accept a transform. Try inline-block instead.");
  return style;
}

function snapshot(element, valueHandlers, transformPagePoint) {
  return {
    layout: snapshotLayout(element, transformPagePoint),
    style: snapshotStyle(element, valueHandlers)
  };
}
/**
 * Calculate an appropriate transform origin for this delta.
 *
 * If components don't change size, it isn't really relavent what origin we provide.
 * When a component is scaling, we want to generate a visually appeasing transform origin and allow
 * the component to scale out (or in) from there. This means 0 for components whose left edge
 * is the same or beyond the `before`, 1 for the inverse, and 0-1 for in between.
 *
 * @param before
 * @param after
 */


function calcOrigin(before, after) {
  var origin = 0.5;
  var beforeSize = before.max - before.min;
  var afterSize = after.max - after.min;

  if (beforeSize > afterSize) {
    origin = progress(before.min, before.max - afterSize, after.min);
  } else if (afterSize > beforeSize) {
    origin = progress(after.min, after.max - beforeSize, before.min);
  }

  return clampProgress(origin);
}

function calcTreeScale(scale, deltas) {
  scale.x = scale.y = 1;
  var numDeltas = deltas.length;

  for (var i = 0; i < numDeltas; i++) {
    var delta = deltas[i];
    scale.x *= delta.x.scale;
    scale.y *= delta.y.scale;
  }
}
/**
 *
 * @param before
 * @param after
 * @param origin
 */


function calcTranslate(before, after, origin) {
  var beforePoint = mix(before.min, before.max, origin);
  var afterPoint = mix(after.min, after.max, origin); //console.log(beforePoint, afterPoint, beforePoint - afterPoint)

  return beforePoint - afterPoint;
}

function scaledPoint(_a, point) {
  var scale = _a.scale,
      originPoint = _a.originPoint;
  var distanceFromOrigin = point - originPoint;
  var scaled = scale * distanceFromOrigin;
  return originPoint + scaled;
}

function calcDelta(delta, before, after, origin) {
  var beforeSize = before.max - before.min;
  var afterSize = after.max - after.min; // TODO: Check this out

  delta.scale = beforeSize / (afterSize || 0.0001);
  delta.origin = origin !== undefined ? origin : calcOrigin(before, after);
  delta.originPoint = after.min + delta.origin * afterSize;
  delta.translate = calcTranslate(before, after, delta.origin); // Clamp

  if (near(delta.scale, 1, 0.0001)) delta.scale = 1;
  if (near(delta.translate)) delta.translate = 0;
}

function calcBoxDelta(delta, before, after, origin) {
  calcDelta(delta.x, before.x, after.x, origin);
  calcDelta(delta.y, before.y, after.y, origin);
}

function applyDelta(point, delta) {
  return scaledPoint(delta, point) + delta.translate;
}

function applyAxisDelta(axis, delta) {
  axis.min = applyDelta(axis.min, delta);
  axis.max = applyDelta(axis.max, delta);
}

function applyBoxDelta(box, delta) {
  //console.log(box.x, delta.x)
  applyAxisDelta(box.x, delta.x);
  applyAxisDelta(box.y, delta.y);
}

function applyTreeDeltas(box, deltas) {
  var numDeltas = deltas.length;

  for (var i = 0; i < numDeltas; i++) {
    applyBoxDelta(box, deltas[i]);
  }
}

function resolve(defaultValue, value) {
  return value === undefined ? defaultValue : resolveMotionValue(value);
}
/**
 * Reset `element.style` to ensure we're not reading styles that have previously been animated.
 * If anything is set in the incoming style prop, use that, otherwise unset to ensure the
 * underlying CSS is read.
 *
 * @param styleProp
 */


function resetStyles(style, valueHandlers) {
  var reset = {
    x: 0,
    y: 0,
    scale: 1,
    scaleX: 1,
    scaleY: 1,
    rotate: 0
  }; // TODO: We need to resolve MotionValues

  for (var key in valueHandlers) {
    var handler = valueHandlers[key];

    if (style[key] !== undefined) {
      reset[key] = style[key];
    } else if (handler.reset) {
      reset[key] = handler.reset(style);
    } else {
      reset[key] = "";
    }
  } // if (offsetSnapshot) {
  //     Object.assign(reset, offsetSnapshot, { position: "absolute" })
  // }


  return reset;
}

function applyCurrent(style, current) {
  for (var key in current) {
    style[key] = current[key];
  }
}

var zeroDelta = {
  translate: 0,
  scale: 1,
  origin: 0,
  originPoint: 0
};

function easeAxis(axis, target, prev, next, p) {
  target[axis].min = mix(prev[axis].min, next[axis].min, p);
  target[axis].max = mix(prev[axis].max, next[axis].max, p);
}

function easeBox(target, prev, next, p) {
  easeAxis("x", target, prev, next, p);
  easeAxis("y", target, prev, next, p);
}

var defaultHandler = {
  snapshotTarget: function snapshotTarget(child) {
    return child.snapshotTarget();
  },
  startAnimation: function startAnimation(child) {
    return child.startAnimation();
  }
};

var batchTransitions = function batchTransitions() {
  var queue = new Set();

  var add = function add(child) {
    return queue.add(child);
  };

  var flush = function flush(_a) {
    var _b = _a === void 0 ? defaultHandler : _a,
        snapshotTarget = _b.snapshotTarget,
        startAnimation = _b.startAnimation;

    if (!queue.size) return;
    var order = Array.from(queue).sort(sortByDepth);
    order.forEach(function (child) {
      return child.resetStyles();
    });
    order.forEach(snapshotTarget);
    order.forEach(startAnimation);
    queue.clear();
  };

  return {
    add: add,
    flush: flush
  };
};

var sortByDepth = function sortByDepth(a, b) {
  return a.depth - b.depth;
};

function near(value, target, maxDistance) {
  if (target === void 0) {
    target = 0;
  }

  if (maxDistance === void 0) {
    maxDistance = 0.01;
  }

  return distance(value, target) < maxDistance;
}
/**
 * @internal
 */


var MotionContext = createContext({
  "static": false,
  magicDepth: -1
});

var isVariantLabel = function isVariantLabel(v) {
  return typeof v === "string" || Array.isArray(v);
};

var isAnimationControls = function isAnimationControls(v) {
  return v instanceof AnimationControls;
};
/**
 * Set up the context for children motion components.
 *
 * We also use this opportunity to apply `initial` values
 */


var useMotionContext = function useMotionContext(parentContext, controls, values, isStatic, _a) {
  if (isStatic === void 0) {
    isStatic = false;
  }

  var initial = _a.initial,
      animate = _a.animate,
      variants = _a.variants,
      whileTap = _a.whileTap,
      whileHover = _a.whileHover,
      layoutId = _a.layoutId;

  var _b;

  var presenceContext = useContext(PresenceContext); // Override initial with that from a parent context, if defined

  if (((_b = presenceContext) === null || _b === void 0 ? void 0 : _b.initial) !== undefined) {
    initial = presenceContext.initial;
  }

  var initialState;

  if (initial === false && !isAnimationControls(animate)) {
    initialState = animate;
  } else if (typeof initial !== "boolean") {
    initialState = initial;
  } // Track mounted status so children can detect whether they were present during their
  // parent's first render


  var hasMounted = useRef(false); // We propagate this component's ValueAnimationControls *if* we're being provided variants,
  // if we're being used to control variants, or if we're being passed animation controls.
  // Otherwise this component should be "invisible" to variant propagation. This is a slight concession
  // to Framer X where every `Frame` is a `motion` component and it might be if we change that in the future
  // that this restriction is removed.

  var shouldPropagateControls = variants || isVariantLabel(animate) || isVariantLabel(whileTap) || isVariantLabel(whileHover) || isAnimationControls(animate); // If this component's `initial` prop is a variant label, propagate it. Otherwise pass the parent's.

  var targetInitial = isVariantLabel(initialState) ? initialState : parentContext.initial; // If this is a variant tree we need to propagate the `animate` prop in case new children are added after
  // the tree initially animates.

  var targetAnimate = isVariantLabel(animate) ? animate : parentContext.animate; // Only allow `initial` to trigger context re-renders if this is a `static` component (ie we're on the Framer canvas)
  // or in another non-animation/interaction environment.

  var initialDependency = isStatic ? targetInitial : null; // Only allow `animate` to trigger context re-renders if it's a variant label. If this is an array of
  // variant labels there's probably an optimisation to deep-compare but it might be an over-optimisation.
  // We want to do this as we rely on React's component rendering order each render cycle to determine
  // the new order of any child components for the `staggerChildren` functionality.

  var animateDependency = shouldPropagateControls && isVariantLabel(targetAnimate) ? targetAnimate : null; // TODO: We need every motion component in the stack to communicate down - for performance we can look into
  // ditching zero deltas if this isn't a motion component

  var magicDelta = useConstant(createZeroDelta);
  var magicDeltas = useRef(__spreadArrays(parentContext.magicDeltas || [], [magicDelta]));
  var magicProgress = useMotionValue(0); // The context to provide to the child. We `useMemo` because although `controls` and `initial` are
  // unlikely to change, by making the context an object it'll be considered a new value every render.
  // So all child motion components will re-render as a result.

  var context = useMemo(function () {
    return {
      controls: shouldPropagateControls ? controls : parentContext.controls,
      initial: targetInitial,
      animate: targetAnimate,
      values: values,
      hasMounted: hasMounted,
      isReducedMotion: parentContext.isReducedMotion,
      magicDepth: // TODO: Make nice isMagic
      animate || layoutId !== undefined ? parentContext.magicDepth + 1 : parentContext.magicDepth,
      magicDelta: magicDelta,
      magicDeltas: magicDeltas.current,
      magicProgress: magicProgress
    };
  }, [initialDependency, animateDependency, parentContext.isReducedMotion, animate, layoutId]); // Update the `static` property every render. This is unlikely to change but also essentially free.

  context["static"] = isStatic; // Set initial state. If this is a static component (ie in Framer canvas), respond to updates
  // in `initial`.

  useInitialOrEveryRender(function () {
    var initialToApply = initialState || parentContext.initial;
    initialToApply && controls.apply(initialToApply);
  }, !isStatic);
  useEffect(function () {
    hasMounted.current = true;
  }, []);
  return context;
};

function createZeroDelta() {
  return {
    x: __assign({}, zeroDelta),
    y: __assign({}, zeroDelta)
  };
}
/**
 * Creates an imperative set of controls to trigger animations.
 *
 * This allows a consolidated, uniform API for animations, to be triggered by other APIs like the `animate` prop, or the gesture handlers.
 *
 * @param values
 * @param props
 * @param ref
 * @param subscribeToParentControls
 *
 * @internal
 */


function useValueAnimationControls(config, props, subscribeToParentControls) {
  var variants = props.variants,
      transition = props.transition;
  var parentControls = useContext(MotionContext).controls;
  var presenceContext = useContext(PresenceContext);
  var controls = useConstant(function () {
    return new ValueAnimationControls(config);
  }); // Reset and resubscribe children every render to ensure stagger order is correct

  if (!presenceContext || presenceContext.isPresent) {
    controls.resetChildren();
    controls.setProps(props);
    controls.setVariants(variants);
    controls.setDefaultTransition(transition);
  } // We have to subscribe to the parent controls within a useEffect rather than during render,
  // as


  useEffect(function () {
    if (subscribeToParentControls && parentControls) {
      parentControls.addChild(controls);
    }
  });
  useEffect(function () {
    return function () {
      // Remove reference to onAnimationComplete from controls. All the MotionValues
      // are unsubscribed from this component separately. We let animations run out
      // as they might be animating other components.
      var onAnimationComplete = props.onAnimationComplete,
          unmountProps = __rest(props, ["onAnimationComplete"]);

      controls.setProps(unmountProps);
      parentControls && parentControls.removeChild(controls);
    };
  }, []);
  return controls;
}

var checkShouldInheritVariant = function checkShouldInheritVariant(_a) {
  var animate = _a.animate,
      variants = _a.variants,
      _b = _a.inherit,
      inherit = _b === void 0 ? true : _b;
  return inherit && !!variants && (!animate || animate instanceof AnimationControls);
};

var isRefObject = function isRefObject(ref) {
  return typeof ref === "object" && ref.hasOwnProperty("current");
};

var session = null;
var syncRenderSession = {
  isOpen: function isOpen() {
    return session !== null;
  },
  open: function open() {
    invariant(!session, "Sync render session already open");
    session = [];
  },
  flush: function flush() {
    invariant(session !== null, "No sync render session found");
    session && session.forEach(function (view) {
      return view.render();
    });
    session = null;
  },
  push: function push(view) {
    invariant(session !== null, "No sync render session found");
    session && session.push(view);
  }
}; // Subscriptions need to be added with unique identifiers to make them concurrent-safe

function subscribe(id, subscribers, sub) {
  subscribers.set(id, sub);
  return function () {
    return subscribers["delete"](id);
  };
}

var NativeElement =
/** @class */
function () {
  function NativeElement(stylerConfig, externalRef) {
    var _this = this;

    this.hasMounted = false; // TODO: We might be able to downsize this as we only have one mount/unmount subscription

    this.mountSubscriptions = new Map();
    this.unmountSubscriptions = new Map();

    this.ref = function (element) {
      if (element !== null && !_this.hasMounted) {
        _this.mount(element);
      } else if (element === null) {
        _this.unmount();
      }
    };

    this.externalRef = externalRef;
    this.stylerConfig = stylerConfig;
  }

  NativeElement.prototype.mount = function (element) {
    var _this = this;

    invariant(element instanceof Element, "No ref found. Ensure components created with motion.custom forward refs using React.forwardRef");
    this.hasMounted = true;
    this.current = element;
    this.styler = styler(element, this.stylerConfig);
    this.mountSubscriptions.forEach(function (sub) {
      return sub(_this);
    });
    this.mountSubscriptions.clear();
    if (!this.externalRef) return;

    if (typeof this.externalRef === "function") {
      this.externalRef(element);
    } else if (isRefObject(this.externalRef)) {
      this.externalRef.current = element;
    }
  };

  NativeElement.prototype.unmount = function () {
    var _this = this;

    this.unmountSubscriptions.forEach(function (sub) {
      return sub(_this);
    });
    this.unmountSubscriptions.clear();
    delete this.current;
    delete this.styler;
    if (!this.externalRef) return;

    if (typeof this.externalRef === "function") {
      this.externalRef(null);
    } else if (isRefObject(this.externalRef)) {
      this.externalRef.current = null;
    }
  };

  NativeElement.prototype.onMount = function (id, sub) {
    return subscribe(id, this.mountSubscriptions, sub);
  };

  NativeElement.prototype.onUnmount = function (id, sub) {
    return subscribe(id, this.unmountSubscriptions, sub);
  };

  NativeElement.prototype.setStyle = function (key, value) {
    this.styler && this.styler.set(key, value);
  };

  NativeElement.prototype.getStyle = function (key) {
    invariant(this.hasMounted, "Attempting to read styles of an unmounted element");
    return this.styler && this.styler.get(key);
  };

  NativeElement.prototype.getInstance = function () {
    return this.current;
  };

  NativeElement.prototype.getBoundingBox = function () {
    return this.current.getBoundingClientRect();
  };

  NativeElement.prototype.getComputedStyle = function () {
    return window.getComputedStyle(this.current);
  };

  NativeElement.prototype.render = function () {
    this.styler.render();
  };

  return NativeElement;
}();

var subscriberSymbol = Symbol("self");

function useNativeElement(values, enableHardwareAcceleration, allowTransformNone, externalRef) {
  if (allowTransformNone === void 0) {
    allowTransformNone = true;
  }

  return useConstant(function () {
    var stylerConfig = {
      preparseOutput: false,
      enableHardwareAcceleration: enableHardwareAcceleration,
      allowTransformNone: allowTransformNone
    };
    var nativeElement = new NativeElement(stylerConfig, externalRef);
    nativeElement.onMount(subscriberSymbol, function () {
      values.mount(function (key, value) {
        nativeElement.setStyle(key, value);

        if (syncRenderSession.isOpen()) {
          syncRenderSession.push(nativeElement);
        }
      });
    });
    nativeElement.onUnmount(subscriberSymbol, function () {
      return values.unmount();
    });
    return nativeElement;
  });
}
/**
 * @internal
 */


var MotionPluginContext = createContext({
  transformPagePoint: function transformPagePoint(p) {
    return p;
  },
  magicValues: {},
  features: []
});
/**
 * @remarks For now I think this should remain a private API for our own use
 * until we can figure out a nicer way of allowing people to add these
 *
 * @internal
 */

function MotionPlugins(_a) {
  var children = _a.children,
      props = __rest(_a, ["children"]);

  var pluginContext = useContext(MotionPluginContext);
  var value = useRef(__assign({}, pluginContext)).current; // Mutative to prevent triggering rerenders in all listening
  // components every time this component renders

  for (var key in props) {
    value[key] = props[key];
  }

  return createElement(MotionPluginContext.Provider, {
    value: value
  }, children);
}
/**
 * @internal
 */


var createMotionComponent = function createMotionComponent(_a) {
  var getValueControlsConfig = _a.getValueControlsConfig,
      loadFeatures = _a.loadFeatures,
      renderComponent = _a.renderComponent;

  function MotionComponent(props, externalRef) {
    var parentContext = useContext(MotionContext);
    var isStatic = parentContext["static"] || props["static"] || false;
    var values = useMotionValues(props);
    var style = useMotionStyles(values, props.style, isStatic, props.transformValues);
    var shouldInheritVariant = checkShouldInheritVariant(props);
    var nativeElement = useNativeElement(values, !isStatic, props.allowTransformNone, externalRef);
    var controlsConfig = useConstant(function () {
      return getValueControlsConfig(nativeElement, values);
    });
    var controls = useValueAnimationControls(controlsConfig, props, shouldInheritVariant);
    var context = useMotionContext(parentContext, controls, values, isStatic, props);
    var plugins = useContext(MotionPluginContext);
    var features = isStatic ? null : loadFeatures(nativeElement, values, props, context, parentContext, controls, shouldInheritVariant, plugins);
    var renderedComponent = renderComponent(nativeElement, style, values, props, isStatic); // The mount order and hierarchy is specific to ensure our element ref is hydrated by the time
    // all plugins and features has to execute.

    return createElement(Fragment, null, createElement(MotionContext.Provider, {
      value: context
    }, renderedComponent), features);
  }

  return forwardRef(MotionComponent);
};
/**
 * @internal
 */


var htmlElements = ["a", "abbr", "address", "area", "article", "aside", "audio", "b", "base", "bdi", "bdo", "big", "blockquote", "body", "br", "button", "canvas", "caption", "cite", "code", "col", "colgroup", "data", "datalist", "dd", "del", "details", "dfn", "dialog", "div", "dl", "dt", "em", "embed", "fieldset", "figcaption", "figure", "footer", "form", "h1", "h2", "h3", "h4", "h5", "h6", "head", "header", "hgroup", "hr", "html", "i", "iframe", "img", "input", "ins", "kbd", "keygen", "label", "legend", "li", "link", "main", "map", "mark", "menu", "menuitem", "meta", "meter", "nav", "noscript", "object", "ol", "optgroup", "option", "output", "p", "param", "picture", "pre", "progress", "q", "rp", "rt", "ruby", "s", "samp", "script", "section", "select", "small", "source", "span", "strong", "style", "sub", "summary", "sup", "table", "tbody", "td", "textarea", "tfoot", "th", "thead", "time", "title", "tr", "track", "u", "ul", "var", "video", "wbr", "webview"];
/**
 * @internal
 */

var svgElements = ["animate", "circle", "clipPath", "defs", "desc", "ellipse", "feBlend", "feColorMatrix", "feComponentTransfer", "feComposite", "feConvolveMatrix", "feDiffuseLighting", "feDisplacementMap", "feDistantLight", "feDropShadow", "feFlood", "feFuncA", "feFuncB", "feFuncG", "feFuncR", "feGaussianBlur", "feImage", "feMerge", "feMergeNode", "feMorphology", "feOffset", "fePointLight", "feSpecularLighting", "feSpotLight", "feTile", "feTurbulence", "filter", "foreignObject", "g", "image", "line", "linearGradient", "marker", "mask", "metadata", "path", "pattern", "polygon", "polyline", "radialGradient", "rect", "stop", "svg", "switch", "symbol", "text", "textPath", "tspan", "use", "view"];

function useUnmountEffect(callback) {
  return useEffect(function () {
    return function () {
      return callback();
    };
  }, []);
}

function addDomEvent(target, eventName, handler, options) {
  if (!handler) return;
  target.addEventListener(eventName, handler, options);
  return function () {
    return target.removeEventListener(eventName, handler, options);
  };
}
/**
 * Attaches an event listener directly to the provided DOM element.
 *
 * Bypassing React's event system can be desirable, for instance when attaching non-passive
 * event handlers.
 *
 * ```jsx
 * const ref = useRef(null)
 *
 * useDomEvent(ref, 'wheel', onWheel, { passive: false })
 *
 * return <div ref={ref} />
 * ```
 *
 * @param ref - React.RefObject that's been provided to the element you want to bind the listener to.
 * @param eventName - Name of the event you want listen for.
 * @param handler - Function to fire when receiving the event.
 * @param options - Options to pass to `Event.addEventListener`.
 *
 * @public
 */


function useDomEvent(ref, eventName, handler, options) {
  useEffect(function () {
    var element = ref.current;

    if (handler && element) {
      return addDomEvent(element, eventName, handler, options);
    }
  }, [ref, eventName, handler, options]);
}

function isMouseEvent(event) {
  // PointerEvent inherits from MouseEvent so we can't use a straight instanceof check.
  if (typeof PointerEvent !== "undefined" && event instanceof PointerEvent) {
    return !!(event.pointerType === "mouse");
  }

  return event instanceof MouseEvent;
}

function isTouchEvent(event) {
  var hasTouches = !!event.touches;
  return hasTouches;
}
/**
 * Filters out events not attached to the primary pointer (currently left mouse button)
 * @param eventHandler
 */


function filterPrimaryPointer(eventHandler) {
  if (!eventHandler) return undefined;
  return function (event) {
    var isMouseEvent = event instanceof MouseEvent;
    var isPrimaryPointer = !isMouseEvent || isMouseEvent && event.button === 0;

    if (isPrimaryPointer) {
      eventHandler(event);
    }
  };
}

var defaultPagePoint = {
  pageX: 0,
  pageY: 0
};

function pointFromTouch(e) {
  var primaryTouch = e.touches[0] || e.changedTouches[0];

  var _a = primaryTouch || defaultPagePoint,
      pageX = _a.pageX,
      pageY = _a.pageY;

  return {
    x: pageX,
    y: pageY
  };
}

function pointFromMouse(_a) {
  var _b = _a.pageX,
      pageX = _b === void 0 ? 0 : _b,
      _c = _a.pageY,
      pageY = _c === void 0 ? 0 : _c;
  return {
    x: pageX,
    y: pageY
  };
}

function extractEventInfo(event) {
  return {
    point: isTouchEvent(event) ? pointFromTouch(event) : pointFromMouse(event)
  };
}

var wrapHandler = function wrapHandler(handler, shouldFilterPrimaryPointer) {
  if (shouldFilterPrimaryPointer === void 0) {
    shouldFilterPrimaryPointer = false;
  }

  if (!handler) return;

  var listener = function listener(event) {
    return handler(event, extractEventInfo(event));
  };

  return shouldFilterPrimaryPointer ? filterPrimaryPointer(listener) : listener;
};

var isBrowser = typeof window !== "undefined"; // We check for event support via functions in case they've been mocked by a testing suite.

var supportsPointerEvents = function supportsPointerEvents() {
  return isBrowser && window.onpointerdown === null;
};

var supportsTouchEvents = function supportsTouchEvents() {
  return isBrowser && window.ontouchstart === null;
};

var supportsMouseEvents = function supportsMouseEvents() {
  return isBrowser && window.onmousedown === null;
};

var mouseEventNames = {
  pointerdown: "mousedown",
  pointermove: "mousemove",
  pointerup: "mouseup",
  pointercancel: "mousecancel",
  pointerover: "mouseover",
  pointerout: "mouseout",
  pointerenter: "mouseenter",
  pointerleave: "mouseleave"
};
var touchEventNames = {
  pointerdown: "touchstart",
  pointermove: "touchmove",
  pointerup: "touchend",
  pointercancel: "touchcancel"
};

function getPointerEventName(name) {
  if (supportsPointerEvents()) {
    return name;
  } else if (supportsTouchEvents()) {
    return touchEventNames[name];
  } else if (supportsMouseEvents()) {
    return mouseEventNames[name];
  }

  return name;
}

function addPointerEvent(target, eventName, handler, options) {
  return addDomEvent(target, getPointerEventName(eventName), wrapHandler(handler, eventName === "pointerdown"), options);
}

function usePointerEvent(ref, eventName, handler, options) {
  return useDomEvent(ref, getPointerEventName(eventName), wrapHandler(handler, eventName === "pointerdown"), options);
}
/** @public */


var Point;

(function (Point) {
  /** @beta */
  Point.subtract = function (a, b) {
    return {
      x: a.x - b.x,
      y: a.y - b.y
    };
  };
  /** @beta */


  Point.relativeTo = function (idOrElem) {
    var elem;

    var getElem = function getElem() {
      // Caching element here could be leaky because of React lifecycle
      if (elem !== undefined) return elem;

      if (typeof idOrElem === "string") {
        elem = document.getElementById(idOrElem);
      } else {
        elem = idOrElem;
      }

      return elem;
    };

    return function (_a) {
      var x = _a.x,
          y = _a.y;
      var localElem = getElem();
      if (!localElem) return undefined;
      var rect = localElem.getBoundingClientRect();
      return {
        x: x - rect.left - window.scrollX,
        y: y - rect.top - window.scrollY
      };
    };
  };
})(Point || (Point = {}));

var isViewportScrollBlocked = false;
var isBrowser$1 = typeof window !== "undefined";

if (isBrowser$1) {
  document.addEventListener("touchmove", function (event) {
    if (isViewportScrollBlocked) {
      event.preventDefault();
    }
  }, {
    passive: false
  });
}

var blockViewportScroll = function blockViewportScroll() {
  return isViewportScrollBlocked = true;
};

var unblockViewportScroll = function unblockViewportScroll() {
  return isViewportScrollBlocked = false;
};
/**
 * @internal
 */


var PanSession =
/** @class */
function () {
  function PanSession(event, handlers, _a) {
    var _this = this;

    var transformPagePoint = (_a === void 0 ? {} : _a).transformPagePoint;
    /**
     * @internal
     */

    this.startEvent = null;
    /**
     * @internal
     */

    this.lastMoveEvent = null;
    /**
     * @internal
     */

    this.lastMoveEventInfo = null;
    /**
     * @internal
     */

    this.handlers = {};

    this.updatePoint = function () {
      if (!(_this.lastMoveEvent && _this.lastMoveEventInfo)) return;
      var info = getPanInfo(_this.lastMoveEventInfo, _this.history);
      var isPanStarted = _this.startEvent !== null; // Only start panning if the offset is larger than 3 pixels. If we make it
      // any larger than this we'll want to reset the pointer history
      // on the first update to avoid visual snapping to the cursoe.

      var isDistancePastThreshold = distance(info.offset, {
        x: 0,
        y: 0
      }) >= 3;
      if (!isPanStarted && !isDistancePastThreshold) return;
      var point = info.point;
      var timestamp = getFrameData().timestamp;

      _this.history.push(__assign(__assign({}, point), {
        timestamp: timestamp
      }));

      var _a = _this.handlers,
          onStart = _a.onStart,
          onMove = _a.onMove;

      if (!isPanStarted) {
        onStart && onStart(_this.lastMoveEvent, info);
        _this.startEvent = _this.lastMoveEvent;
      }

      onMove && onMove(_this.lastMoveEvent, info);
    }; // If we have more than one touch, don't start detecting this gesture


    if (isTouchEvent(event) && event.touches.length > 1) return;
    this.handlers = handlers;
    this.transformPagePoint = transformPagePoint;
    var info = extractEventInfo(event);
    var initialInfo = transformPoint(info, this.transformPagePoint);
    var point = initialInfo.point;
    var timestamp = getFrameData().timestamp;
    this.history = [__assign(__assign({}, point), {
      timestamp: timestamp
    })];
    var onSessionStart = handlers.onSessionStart;
    onSessionStart && onSessionStart(event, getPanInfo(initialInfo, this.history));
    var removeOnPointerMove = addPointerEvent(window, "pointermove", function (event, info) {
      return _this.handlePointerMove(event, info);
    });
    var removeOnPointerUp = addPointerEvent(window, "pointerup", function (event, info) {
      return _this.handlePointerUp(event, info);
    });

    this.removeListeners = function () {
      removeOnPointerMove && removeOnPointerMove();
      removeOnPointerUp && removeOnPointerUp();
    };
  }

  PanSession.prototype.handlePointerMove = function (event, info) {
    this.lastMoveEvent = event;
    this.lastMoveEventInfo = transformPoint(info, this.transformPagePoint); // Because Safari doesn't trigger mouseup events when it's above a `<select>`

    if (isMouseEvent(event) && event.buttons === 0) {
      this.handlePointerUp(event, info);
      return;
    } // Throttle mouse move event to once per frame


    sync.update(this.updatePoint, true);
  };

  PanSession.prototype.handlePointerUp = function (event, info) {
    this.end();
    var onEnd = this.handlers.onEnd;
    if (!onEnd) return;
    var panInfo = getPanInfo(transformPoint(info, this.transformPagePoint), this.history);
    onEnd && onEnd(event, panInfo);
  };

  PanSession.prototype.updateHandlers = function (handlers) {
    this.handlers = handlers;
  };

  PanSession.prototype.end = function () {
    this.removeListeners && this.removeListeners();
    cancelSync.update(this.updatePoint);
    unblockViewportScroll();
  };

  return PanSession;
}();

function transformPoint(info, transformPagePoint) {
  return transformPagePoint ? {
    point: transformPagePoint(info.point)
  } : info;
}

function getPanInfo(_a, history) {
  var point = _a.point;
  return {
    point: point,
    delta: Point.subtract(point, lastDevicePoint(history)),
    offset: Point.subtract(point, startDevicePoint(history)),
    velocity: getVelocity$1(history, 0.1)
  };
}

function startDevicePoint(history) {
  return history[0];
}

function lastDevicePoint(history) {
  return history[history.length - 1];
}

function getVelocity$1(history, timeDelta) {
  if (history.length < 2) {
    return {
      x: 0,
      y: 0
    };
  }

  var i = history.length - 1;
  var timestampedPoint = null;
  var lastPoint = lastDevicePoint(history);

  while (i >= 0) {
    timestampedPoint = history[i];

    if (lastPoint.timestamp - timestampedPoint.timestamp > secondsToMilliseconds(timeDelta)) {
      break;
    }

    i--;
  }

  if (!timestampedPoint) {
    return {
      x: 0,
      y: 0
    };
  }

  var time = (lastPoint.timestamp - timestampedPoint.timestamp) / 1000;

  if (time === 0) {
    return {
      x: 0,
      y: 0
    };
  }

  var currentVelocity = {
    x: (lastPoint.x - timestampedPoint.x) / time,
    y: (lastPoint.y - timestampedPoint.y) / time
  };

  if (currentVelocity.x === Infinity) {
    currentVelocity.x = 0;
  }

  if (currentVelocity.y === Infinity) {
    currentVelocity.y = 0;
  }

  return currentVelocity;
}
/**
 *
 * @param handlers -
 * @param ref -
 *
 * @internalremarks
 * Currently this sets new pan gesture functions every render. The memo route has been explored
 * in the past but ultimately we're still creating new functions every render. An optimisation
 * to explore is creating the pan gestures and loading them into a `ref`.
 *
 * @internal
 */


function usePanGesture(_a, ref) {
  var onPan = _a.onPan,
      onPanStart = _a.onPanStart,
      onPanEnd = _a.onPanEnd,
      onPanSessionStart = _a.onPanSessionStart;
  var hasPanEvents = onPan || onPanStart || onPanEnd || onPanSessionStart;
  var panSession = useRef(null);
  var transformPagePoint = useContext(MotionPluginContext).transformPagePoint;
  var handlers = {
    onSessionStart: onPanSessionStart,
    onStart: onPanStart,
    onMove: onPan,
    onEnd: function onEnd(event, info) {
      panSession.current = null;
      onPanEnd && onPanEnd(event, info);
    }
  };

  if (panSession.current !== null) {
    panSession.current.updateHandlers(handlers);
  }

  function onPointerDown(event) {
    panSession.current = new PanSession(event, handlers, {
      transformPagePoint: transformPagePoint
    });
  }

  usePointerEvent(ref, "pointerdown", hasPanEvents && onPointerDown);
  useUnmountEffect(function () {
    return panSession.current && panSession.current.end();
  });
}
/**
 * Recursively traverse up the tree to check whether the provided child node
 * is the parent or a descendant of it.
 *
 * @param parent - Element to find
 * @param child - Element to test against parent
 */


var isNodeOrChild = function isNodeOrChild(parent, child) {
  if (!child) {
    return false;
  } else if (parent === child) {
    return true;
  } else {
    return isNodeOrChild(parent, child.parentElement);
  }
};

var order = ["whileHover", "whileTap", "whileDrag"];

var getGesturePriority = function getGesturePriority(gesture) {
  return order.indexOf(gesture) + 1;
};

function createLock(name) {
  var lock = null;
  return function () {
    var openLock = function openLock() {
      lock = null;
    };

    if (lock === null) {
      lock = name;
      return openLock;
    }

    return false;
  };
}

var globalHorizontalLock = createLock("dragHorizontal");
var globalVerticalLock = createLock("dragVertical");

function getGlobalLock(drag) {
  var lock = false;

  if (drag === "y") {
    lock = globalVerticalLock();
  } else if (drag === "x") {
    lock = globalHorizontalLock();
  } else {
    var openHorizontal_1 = globalHorizontalLock();
    var openVertical_1 = globalVerticalLock();

    if (openHorizontal_1 && openVertical_1) {
      lock = function lock() {
        openHorizontal_1();
        openVertical_1();
      };
    } else {
      // Release the locks because we don't use them
      if (openHorizontal_1) openHorizontal_1();
      if (openVertical_1) openVertical_1();
    }
  }

  return lock;
}

var tapGesturePriority = getGesturePriority("whileTap");
/**
 * @param handlers -
 * @internal
 */

function useTapGesture(_a, ref) {
  var onTap = _a.onTap,
      onTapStart = _a.onTapStart,
      onTapCancel = _a.onTapCancel,
      whileTap = _a.whileTap,
      controls = _a.controls;
  var hasTapListeners = onTap || onTapStart || onTapCancel || whileTap;
  var isTapping = useRef(false);
  var cancelPointerEventListener = useRef(null);

  function removePointerUp() {
    cancelPointerEventListener.current && cancelPointerEventListener.current();
    cancelPointerEventListener.current = null;
  }

  if (whileTap && controls) {
    controls.setOverride(whileTap, tapGesturePriority);
  } // We load this event handler into a ref so we can later refer to
  // onPointerUp.current which will always have reference to the latest props


  var onPointerUp = useRef(null);

  onPointerUp.current = function (event, info) {
    var element = ref.current;
    removePointerUp();
    if (!isTapping.current || !element) return;
    isTapping.current = false;

    if (controls && whileTap) {
      controls.clearOverride(tapGesturePriority);
    } // Check the gesture lock - if we get it, it means no drag gesture is active
    // and we can safely fire the tap gesture.


    var openGestureLock = getGlobalLock(true);
    if (!openGestureLock) return;
    openGestureLock();

    if (!isNodeOrChild(element, event.target)) {
      onTapCancel && onTapCancel(event, info);
    } else {
      onTap && onTap(event, info);
    }
  };

  function onPointerDown(event, info) {
    removePointerUp();
    cancelPointerEventListener.current = addPointerEvent(window, "pointerup", function (event, info) {
      return onPointerUp.current(event, info);
    });
    var element = ref.current;
    if (!element || isTapping.current) return;
    isTapping.current = true;
    onTapStart && onTapStart(event, info);

    if (controls && whileTap) {
      controls.startOverride(tapGesturePriority);
    }
  }

  usePointerEvent(ref, "pointerdown", hasTapListeners ? onPointerDown : undefined);
  useUnmountEffect(removePointerUp);
}

var hoverPriority = getGesturePriority("whileHover");

var filterTouch = function filterTouch(listener) {
  return function (event, info) {
    if (isMouseEvent(event)) listener(event, info);
  };
};
/**
 *
 * @param props
 * @param ref
 * @internal
 */


function useHoverGesture(_a, ref) {
  var whileHover = _a.whileHover,
      onHoverStart = _a.onHoverStart,
      onHoverEnd = _a.onHoverEnd,
      controls = _a.controls;

  if (whileHover && controls) {
    controls.setOverride(whileHover, hoverPriority);
  }

  usePointerEvent(ref, "pointerenter", filterTouch(function (event, info) {
    if (onHoverStart) onHoverStart(event, info);

    if (whileHover && controls) {
      controls.startOverride(hoverPriority);
    }
  }));
  usePointerEvent(ref, "pointerleave", filterTouch(function (event, info) {
    if (onHoverEnd) onHoverEnd(event, info);

    if (whileHover && controls) {
      controls.clearOverride(hoverPriority);
    }
  }));
}
/**
 * Add pan and tap gesture recognition to an element.
 *
 * @param props - Gesture event handlers
 * @param ref - React `ref` containing a DOM `Element`
 * @public
 */


function useGestures(props, ref) {
  usePanGesture(props, ref);
  useTapGesture(props, ref);
  useHoverGesture(props, ref);
}

var makeRenderlessComponent = function makeRenderlessComponent(hook) {
  return function (props) {
    hook(props);
    return null;
  };
};

var gestureProps = ["onPan", "onPanStart", "onPanEnd", "onPanSessionStart", "onTap", "onTapStart", "onTapCancel", "whileTap", "whileHover", "onHoverStart", "onHoverEnd"];
var Gestures = {
  key: "gestures",
  shouldRender: function shouldRender(props) {
    return gestureProps.some(function (key) {
      return props.hasOwnProperty(key);
    });
  },
  Component: makeRenderlessComponent(function (_a) {
    var nativeElement = _a.nativeElement,
        props = __rest(_a, ["nativeElement"]);

    useGestures(props, nativeElement);
  })
};

var noop = function noop(v) {
  return v;
};

var ComponentDragControls =
/** @class */
function () {
  function ComponentDragControls(_a) {
    var nativeElement = _a.nativeElement,
        values = _a.values,
        controls = _a.controls;
    /**
     * Track whether we're currently dragging.
     *
     * @internal
     */

    this.isDragging = false;
    /**
     * The current direction of drag, or `null` if both.
     *
     * @internal
     */

    this.currentDirection = null;
    /**
     * The permitted t/r/b/l boundaries of travel, in pixels.
     *
     * @internal
     */

    this.constraints = false;
    /**
     * A reference to the host component's latest props.
     *
     * @internal
     */

    this.props = {
      transformPagePoint: noop
    };
    /**
     * References to the MotionValues used for tracking the current dragged point.
     *
     * @internal
     */

    this.point = {};
    /**
     * The origin point for the current drag gesture.
     *
     * @internal
     */

    this.origin = {
      x: motionValue(0),
      y: motionValue(0)
    }; // This is a reference to the global drag gesture lock, ensuring only one component
    // can "capture" the drag of one or both axes.
    // TODO: Look into moving this into pansession?

    this.openGlobalLock = null;
    /**
     * @internal
     */

    this.panSession = null;
    /**
     * A reference to the previous constraints bounding box
     *
     * @internal
     */

    this.prevConstraintsBox = {
      width: 0,
      height: 0,
      x: 0,
      y: 0
    };
    this.nativeElement = nativeElement;
    this.values = values;
    this.controls = controls;
  }
  /**
   * Start dragging the host component.
   *
   * @param event - The originating pointer event.
   * @param options -
   *
   * @public
   */


  ComponentDragControls.prototype.start = function (originEvent, _a) {
    var _this = this;

    var _b = (_a === void 0 ? {} : _a).snapToCursor,
        snapToCursor = _b === void 0 ? false : _b;
    snapToCursor && this.snapToCursor(originEvent);

    var onSessionStart = function onSessionStart() {
      // Initiate viewport scroll blocking on touch start. This is a very aggressive approach
      // which has come out of the difficulty in us being able to do this once a scroll gesture
      // has initiated in mobile browsers. This means if there's a horizontally-scrolling carousel
      // on a page we can't let a user scroll the page itself from it. Ideally what we'd do is
      // trigger this once we've got a scroll direction determined. This approach sort-of worked
      // but if the component was dragged too far in a single frame page scrolling would initiate.
      blockViewportScroll(); // Stop any animations on both axis values immediately. This allows the user to throw and catch
      // the component.

      _this.stopMotion();
    };

    var onStart = function onStart(event, info) {
      // If constraints are an element, resolve them again in case they've updated.
      if (_this.constraintsNeedResolution) {
        var _a = _this.props,
            dragConstraints = _a.dragConstraints,
            transformPagePoint_1 = _a.transformPagePoint;
        _this.constraints = calculateConstraintsFromDom(dragConstraints.current, _this.nativeElement.getInstance(), _this.point, transformPagePoint_1);

        _this.applyConstraintsToPoint();
      } // Set point origin and stop any existing animations.


      bothAxis(function (axis) {
        var axisPoint = _this.point[axis];
        if (!axisPoint) return;

        _this.origin[axis].set(axisPoint.get());
      }); // Attempt to grab the global drag gesture lock - maybe make this part of PanSession

      var _b = _this.props,
          drag = _b.drag,
          dragPropagation = _b.dragPropagation;

      if (drag && !dragPropagation) {
        if (_this.openGlobalLock) _this.openGlobalLock();
        _this.openGlobalLock = getGlobalLock(drag);
        if (!_this.openGlobalLock) return;
      }

      _this.isDragging = true;
      _this.currentDirection = null;
      var onDragStart = _this.props.onDragStart;
      onDragStart && onDragStart(event, convertPanToDrag(info, _this.point));
    };

    var onMove = function onMove(event, info) {
      var _a = _this.props,
          dragPropagation = _a.dragPropagation,
          dragDirectionLock = _a.dragDirectionLock; // If we didn't successfully receive the gesture lock, early return.

      if (!dragPropagation && !_this.openGlobalLock) return;
      var offset = info.offset; // Attempt to detect drag direction if directionLock is true

      if (dragDirectionLock && _this.currentDirection === null) {
        _this.currentDirection = getCurrentDirection(offset); // If we've successfully set a direction, notify listener

        if (_this.currentDirection !== null) {
          var onDirectionLock = _this.props.onDirectionLock;
          onDirectionLock && onDirectionLock(_this.currentDirection);
        }

        return;
      }

      _this.updatePoint("x", offset);

      _this.updatePoint("y", offset);

      var onDrag = _this.props.onDrag;
      onDrag && onDrag(event, convertPanToDrag(info, _this.point));
    };

    var onEnd = function onEnd(event, info) {
      _this.stop(event, info);
    };

    var transformPagePoint = this.props.transformPagePoint;
    this.panSession = new PanSession(originEvent, {
      onSessionStart: onSessionStart,
      onStart: onStart,
      onMove: onMove,
      onEnd: onEnd
    }, {
      transformPagePoint: transformPagePoint
    });
  };

  ComponentDragControls.prototype.cancelDrag = function () {
    unblockViewportScroll();
    this.isDragging = false;
    this.panSession && this.panSession.end();
    this.panSession = null;

    if (!this.props.dragPropagation && this.openGlobalLock) {
      this.openGlobalLock();
      this.openGlobalLock = null;
    }
  };

  ComponentDragControls.prototype.stop = function (event, info) {
    var _a;

    (_a = this.panSession) === null || _a === void 0 ? void 0 : _a.end();
    this.panSession = null;
    var isDragging = this.isDragging;
    this.cancelDrag();
    if (!isDragging) return;
    var _b = this.props,
        dragMomentum = _b.dragMomentum,
        dragElastic = _b.dragElastic,
        onDragEnd = _b.onDragEnd;

    if (dragMomentum || dragElastic) {
      var velocity = info.velocity;
      this.animateDragEnd(velocity);
    } else {
      this.recordBoxInfo(this.constraints);
    }

    onDragEnd && onDragEnd(event, convertPanToDrag(info, this.point));
  };

  ComponentDragControls.prototype.recordBoxInfo = function (constraints) {
    if (constraints) {
      var right = constraints.right,
          left = constraints.left,
          bottom = constraints.bottom,
          top_1 = constraints.top;
      this.prevConstraintsBox.width = (right || 0) - (left || 0);
      this.prevConstraintsBox.height = (bottom || 0) - (top_1 || 0);
    }

    if (this.point.x) this.prevConstraintsBox.x = this.point.x.get();
    if (this.point.y) this.prevConstraintsBox.y = this.point.y.get();
  };

  ComponentDragControls.prototype.snapToCursor = function (event) {
    var _this = this;

    var transformPagePoint = this.props.transformPagePoint;
    var point = extractEventInfo(event).point;
    var boundingBox = getBoundingBox(this.nativeElement.getInstance(), transformPagePoint);
    var center = {
      x: boundingBox.width / 2 + boundingBox.left + window.scrollX,
      y: boundingBox.height / 2 + boundingBox.top + window.scrollY
    };
    var offset = {
      x: point.x - center.x,
      y: point.y - center.y
    };
    bothAxis(function (axis) {
      var point = _this.point[axis];
      if (!point) return;

      _this.origin[axis].set(point.get());
    });
    this.updatePoint("x", offset);
    this.updatePoint("y", offset);
  };

  ComponentDragControls.prototype.setPoint = function (axis, value) {
    this.point[axis] = value;
  };

  ComponentDragControls.prototype.updatePoint = function (axis, offset) {
    var _a = this.props,
        drag = _a.drag,
        dragElastic = _a.dragElastic;
    var axisPoint = this.point[axis]; // If we're not dragging this axis, do an early return.

    if (!shouldDrag(axis, drag, this.currentDirection) || !axisPoint) return;
    var current = applyConstraints(axis, this.origin[axis].get() + offset[axis], this.constraints, dragElastic);
    axisPoint.set(current);
  };

  ComponentDragControls.prototype.updateProps = function (_a) {
    var _this = this;

    var _b = _a.drag,
        drag = _b === void 0 ? false : _b,
        _c = _a.dragDirectionLock,
        dragDirectionLock = _c === void 0 ? false : _c,
        _d = _a.dragPropagation,
        dragPropagation = _d === void 0 ? false : _d,
        _e = _a.dragConstraints,
        dragConstraints = _e === void 0 ? false : _e,
        _f = _a.dragElastic,
        dragElastic = _f === void 0 ? true : _f,
        _g = _a.dragMomentum,
        dragMomentum = _g === void 0 ? true : _g,
        remainingProps = __rest(_a, ["drag", "dragDirectionLock", "dragPropagation", "dragConstraints", "dragElastic", "dragMomentum"]);

    this.props = __assign({
      drag: drag,
      dragDirectionLock: dragDirectionLock,
      dragPropagation: dragPropagation,
      dragConstraints: dragConstraints,
      dragElastic: dragElastic,
      dragMomentum: dragMomentum
    }, remainingProps);
    var _dragValueX = remainingProps._dragValueX,
        _dragValueY = remainingProps._dragValueY,
        dragOriginX = remainingProps.dragOriginX,
        dragOriginY = remainingProps.dragOriginY;
    if (dragOriginX) this.origin.x = dragOriginX;
    if (dragOriginY) this.origin.y = dragOriginY; // Get the `MotionValue` for both draggable axes, or create them if they don't already
    // exist on this component.

    bothAxis(function (axis) {
      if (!shouldDrag(axis, drag, _this.currentDirection)) return;
      var defaultValue = axis === "x" ? _dragValueX : _dragValueY;

      _this.setPoint(axis, defaultValue || _this.values.get(axis, 0));
    }); // If `dragConstraints` is a React `ref`, we should resolve the constraints once the
    // component has rendered.

    this.constraintsNeedResolution = isRefObject(dragConstraints);
    this.constraints = this.constraintsNeedResolution ? this.constraints || false : dragConstraints;
  };

  ComponentDragControls.prototype.applyConstraintsToPoint = function (constraints) {
    var _this = this;

    if (constraints === void 0) {
      constraints = this.constraints;
    }

    return bothAxis(function (axis) {
      var axisPoint = _this.point[axis];
      axisPoint && !axisPoint.isAnimating() && applyConstraints(axis, axisPoint, constraints, 0);
    });
  };

  ComponentDragControls.prototype.animateDragEnd = function (velocity) {
    var _this = this;

    var _a = this.props,
        drag = _a.drag,
        dragMomentum = _a.dragMomentum,
        dragElastic = _a.dragElastic,
        dragTransition = _a.dragTransition,
        _dragValueX = _a._dragValueX,
        _dragValueY = _a._dragValueY,
        _dragTransitionControls = _a._dragTransitionControls;
    var momentumAnimations = bothAxis(function (axis) {
      var _a;

      if (!shouldDrag(axis, drag, _this.currentDirection)) {
        return;
      }

      var transition = _this.constraints ? getConstraints(axis, _this.constraints) : {};
      /**
       * Overdamp the boundary spring if `dragElastic` is disabled. There's still a frame
       * of spring animations so we should look into adding a disable spring option to `inertia`.
       * We could do something here where we affect the `bounceStiffness` and `bounceDamping`
       * using the value of `dragElastic`.
       */

      var bounceStiffness = dragElastic ? 200 : 1000000;
      var bounceDamping = dragElastic ? 40 : 10000000;
      var animationControls = _dragTransitionControls || _this.controls;

      var inertia = __assign(__assign({
        type: "inertia",
        velocity: dragMomentum ? velocity[axis] : 0,
        bounceStiffness: bounceStiffness,
        bounceDamping: bounceDamping,
        timeConstant: 750,
        restDelta: 1
      }, dragTransition), transition);

      var externalAxisMotionValue = axis === "x" ? _dragValueX : _dragValueY; // If we're not animating on an externally-provided `MotionValue` we can use the
      // component's animation controls which will handle interactions with whileHover (etc),
      // otherwise we just have to animate the `MotionValue` itself.

      return externalAxisMotionValue ? startAnimation(axis, externalAxisMotionValue, 0, inertia) : animationControls.start((_a = {}, _a[axis] = 0, _a.transition = inertia, _a));
    }); // Run all animations and then resolve the new drag constraints.

    return Promise.all(momentumAnimations).then(function () {
      _this.recordBoxInfo(_this.constraints);

      _this.scalePoint();

      var onDragTransitionEnd = _this.props.onDragTransitionEnd;
      onDragTransitionEnd && onDragTransitionEnd();
    });
  };

  ComponentDragControls.prototype.stopMotion = function () {
    var _this = this;

    bothAxis(function (axis) {
      var axisPoint = _this.point[axis];
      axisPoint && axisPoint.stop();
    });
  };

  ComponentDragControls.prototype.scalePoint = function () {
    var _this = this;

    var _a = this.props,
        dragConstraints = _a.dragConstraints,
        transformPagePoint = _a.transformPagePoint;
    if (!isRefObject(dragConstraints)) return;
    var constraintsBox = getBoundingBox(dragConstraints.current, transformPagePoint);
    var draggableBox = getBoundingBox(this.nativeElement.getInstance(), transformPagePoint); // Scale a point relative to the transformation of a constraints-providing element.

    var scaleAxisPoint = function scaleAxisPoint(axis, dimension) {
      var pointToScale = _this.point[axis];
      if (!pointToScale) return; // Stop any current animations as they bug out if you resize during one

      if (pointToScale.isAnimating()) {
        pointToScale.stop();

        _this.recordBoxInfo();

        return;
      } // If the previous dimension was `0` (default), set `scale` to `1` to prevent
      // divide by zero errors.


      var scale = _this.prevConstraintsBox[dimension] ? (constraintsBox[dimension] - draggableBox[dimension]) / _this.prevConstraintsBox[dimension] : 1;
      pointToScale.set(_this.prevConstraintsBox[axis] * scale);
    };

    scaleAxisPoint("x", "width");
    scaleAxisPoint("y", "height");
  };

  ComponentDragControls.prototype.mount = function (element) {
    var _this = this;

    var stopPointerListener = addPointerEvent(element, "pointerdown", function (event) {
      var _a = _this.props,
          drag = _a.drag,
          _b = _a.dragListener,
          dragListener = _b === void 0 ? true : _b;
      drag && dragListener && _this.start(event);
    });
    var stopResizeListener = addDomEvent(window, "resize", function () {
      return _this.scalePoint();
    });

    if (this.constraintsNeedResolution) {
      var _a = this.props,
          dragConstraints = _a.dragConstraints,
          transformPagePoint = _a.transformPagePoint;
      var constraints = calculateConstraintsFromDom(dragConstraints.current, this.nativeElement.getInstance(), this.point, transformPagePoint);
      this.applyConstraintsToPoint(constraints);
      this.recordBoxInfo(constraints);
    } else if (!this.isDragging && this.constraints) {
      this.applyConstraintsToPoint();
    }

    return function () {
      stopPointerListener && stopPointerListener();
      stopResizeListener && stopResizeListener();

      _this.cancelDrag();
    };
  };

  return ComponentDragControls;
}(); // Call a handler once for each axis


function bothAxis(handler) {
  return [handler("x"), handler("y")];
}

function convertPanToDrag(info, point) {
  return __assign(__assign({}, info), {
    point: {
      x: point.x ? point.x.get() : 0,
      y: point.y ? point.y.get() : 0
    }
  });
}

function getConstraints(axis, _a) {
  var top = _a.top,
      right = _a.right,
      bottom = _a.bottom,
      left = _a.left;

  if (axis === "x") {
    return {
      min: left,
      max: right
    };
  } else {
    return {
      min: top,
      max: bottom
    };
  }
}

function shouldDrag(direction, drag, currentDirection) {
  return (drag === true || drag === direction) && (currentDirection === null || currentDirection === direction);
}
/**
 * Based on an x/y offset determine the current drag direction. If both axis' offsets are lower
 * than the provided threshold, return `null`.
 *
 * @param offset - The x/y offset from origin.
 * @param lockThreshold - (Optional) - the minimum absolute offset before we can determine a drag direction.
 */


function getCurrentDirection(offset, lockThreshold) {
  if (lockThreshold === void 0) {
    lockThreshold = 10;
  }

  var direction = null;

  if (Math.abs(offset.y) > lockThreshold) {
    direction = "y";
  } else if (Math.abs(offset.x) > lockThreshold) {
    direction = "x";
  }

  return direction;
}
/**
 * Takes a parent Element and a draggable Element and returns pixel-based drag constraints.
 *
 * @param constraintsRef
 * @param draggableRef
 */


function calculateConstraintsFromDom(constraintsElement, draggableElement, point, transformPagePoint) {
  invariant(constraintsElement !== null && draggableElement !== null, "If `dragConstraints` is set as a React ref, that ref must be passed to another component's `ref` prop.");
  var parentBoundingBox = getBoundingBox(constraintsElement, transformPagePoint);
  var draggableBoundingBox = getBoundingBox(draggableElement, transformPagePoint);
  var left = parentBoundingBox.left - draggableBoundingBox.left + getCurrentOffset(point.x);
  var top = parentBoundingBox.top - draggableBoundingBox.top + getCurrentOffset(point.y);
  var right = parentBoundingBox.width - draggableBoundingBox.width + left;
  var bottom = parentBoundingBox.height - draggableBoundingBox.height + top;
  return {
    top: top,
    left: left,
    right: right,
    bottom: bottom
  };
}

function getBoundingBox(element, transformPagePoint) {
  var rect = element.getBoundingClientRect();

  var _a = transformPagePoint({
    x: rect.left,
    y: rect.top
  }),
      left = _a.x,
      top = _a.y;

  var _b = transformPagePoint({
    x: rect.width,
    y: rect.height
  }),
      width = _b.x,
      height = _b.y;

  return {
    left: left,
    top: top,
    width: width,
    height: height
  };
}

function getCurrentOffset(point) {
  return point ? point.get() : 0;
}

function applyConstraints(axis, value, constraints, dragElastic) {
  var constrainedValue = value instanceof MotionValue ? value.get() : value;

  if (!constraints) {
    return constrainedValue;
  }

  var _a = getConstraints(axis, constraints),
      min = _a.min,
      max = _a.max;

  if (min !== undefined && constrainedValue < min) {
    constrainedValue = dragElastic ? applyOverdrag(min, constrainedValue, dragElastic) : Math.max(min, constrainedValue);
  } else if (max !== undefined && constrainedValue > max) {
    constrainedValue = dragElastic ? applyOverdrag(max, constrainedValue, dragElastic) : Math.min(max, constrainedValue);
  }

  if (value instanceof MotionValue) {
    value.set(constrainedValue);
  }

  return constrainedValue;
}

function applyOverdrag(origin, current, dragElastic) {
  var dragFactor = typeof dragElastic === "number" ? dragElastic : 0.35;
  return mix(origin, current, dragFactor);
}
/**
 * When a component is the child of `AnimatePresence`, it can use `usePresence`
 * to access information about whether it's still present in the React tree.
 *
 * ```jsx
 * import { usePresence } from "framer-motion"
 *
 * export const Component = () => {
 *   const [isPresent, safeToRemove] = usePresence()
 *
 *   useEffect(() => {
 *     !isPresent setTimeout(safeToRemove, 1000)
 *   }, [isPresent])
 *
 *   return <div />
 * }
 * ```
 *
 * If `isPresent` is `false`, it means that a component has been removed the tree, but
 * `AnimatePresence` won't really remove it until `safeToRemove` has been called.
 *
 * @public
 */


function usePresence() {
  var context = useContext(PresenceContext);
  if (context === null) return [true, null];
  var isPresent = context.isPresent,
      onExitComplete = context.onExitComplete,
      register = context.register;
  useEffect(register, []);
  return !isPresent && onExitComplete ? [false, onExitComplete] : [true];
}
/**
 * A hook that allows an element to be dragged.
 *
 * @param param
 * @param ref
 * @param values
 * @param controls
 *
 * @internal
 */


function useDrag(props, nativeElement, values, controls) {
  var groupDragControls = props.dragControls;
  var transformPagePoint = useContext(MotionPluginContext).transformPagePoint;
  var dragControls = useConstant(function () {
    return new ComponentDragControls({
      nativeElement: nativeElement,
      values: values,
      controls: controls
    });
  });
  dragControls.updateProps(__assign(__assign({}, props), {
    transformPagePoint: transformPagePoint
  }));
  useDisableDragOnExit(dragControls);
  useEffect(function () {
    return groupDragControls && groupDragControls.subscribe(dragControls);
  }, [dragControls]);
  useEffect(function () {
    return dragControls.mount(nativeElement.getInstance());
  }, []);
}

function useDisableDragOnExit(dragControls) {
  var _a = usePresence(),
      isPresent = _a[0],
      safeToRemove = _a[1];

  useEffect(function () {
    if (!isPresent && safeToRemove) {
      safeToRemove();
      dragControls.stopMotion();
    }
  }, [isPresent]);
}

var Drag = {
  key: "drag",
  shouldRender: function shouldRender(props) {
    return !!props.drag;
  },
  Component: makeRenderlessComponent(function (_a) {
    var nativeElement = _a.nativeElement,
        values = _a.values,
        controls = _a.controls,
        props = __rest(_a, ["nativeElement", "values", "controls"]);

    return useDrag(props, nativeElement, values, controls);
  })
};

function isCSSVariable(value) {
  return typeof value === "string" && value.startsWith("var(--");
}
/**
 * Parse Framer's special CSS variable format into a CSS token and a fallback.
 *
 * ```
 * `var(--foo, #fff)` => [`--foo`, '#fff']
 * ```
 *
 * @param current
 */


var cssVariableRegex = /var\((--[a-zA-Z0-9-_]+),? ?([a-zA-Z0-9 ()%#.,-]+)?\)/;

function parseCSSVariable(current) {
  var match = cssVariableRegex.exec(current);
  if (!match) return [,];
  var token = match[1],
      fallback = match[2];
  return [token, fallback];
}

var maxDepth = 4;

function getVariableValue(current, element, depth) {
  if (depth === void 0) {
    depth = 1;
  }

  invariant(depth <= maxDepth, "Max CSS variable fallback depth detected in property \"" + current + "\". This may indicate a circular fallback dependency.");

  var _a = parseCSSVariable(current),
      token = _a[0],
      fallback = _a[1]; // No CSS variable detected


  if (!token) return; // Attempt to read this CSS variable off the element

  var resolved = window.getComputedStyle(element).getPropertyValue(token);

  if (resolved) {
    return resolved;
  } else if (isCSSVariable(fallback)) {
    // The fallback might itself be a CSS variable, in which case we attempt to resolve it too.
    return getVariableValue(fallback, element, depth + 1);
  } else {
    return fallback;
  }
}
/**
 * Resolve CSS variables from
 *
 * @internal
 */


function resolveCSSVariables(values, nativeElement, _a, transitionEnd) {
  var target = __rest(_a, []);

  var element = nativeElement.getInstance();
  if (!(element instanceof HTMLElement)) return {
    target: target,
    transitionEnd: transitionEnd
  }; // If `transitionEnd` isn't `undefined`, clone it. We could clone `target` and `transitionEnd`
  // only if they change but I think this reads clearer and this isn't a performance-critical path.

  if (transitionEnd) {
    transitionEnd = __assign({}, transitionEnd);
  } // Go through existing `MotionValue`s and ensure any existing CSS variables are resolved


  values.forEach(function (value) {
    var current = value.get();
    if (!isCSSVariable(current)) return;
    var resolved = getVariableValue(current, element);
    if (resolved) value.set(resolved);
  }); // Cycle through every target property and resolve CSS variables. Currently
  // we only read single-var properties like `var(--foo)`, not `calc(var(--foo) + 20px)`

  for (var key in target) {
    var current = target[key];
    if (!isCSSVariable(current)) continue;
    var resolved = getVariableValue(current, element);
    if (!resolved) continue; // Clone target if it hasn't already been

    target[key] = resolved; // If the user hasn't already set this key on `transitionEnd`, set it to the unresolved
    // CSS variable. This will ensure that after the animation the component will reflect
    // changes in the value of the CSS variable.

    if (transitionEnd && transitionEnd[key] === undefined) {
      transitionEnd[key] = current;
    }
  }

  return {
    target: target,
    transitionEnd: transitionEnd
  };
}

var positionalKeys = new Set(["width", "height", "top", "left", "right", "bottom", "x", "y"]);

var isPositionalKey = function isPositionalKey(key) {
  return positionalKeys.has(key);
};

var hasPositionalKey = function hasPositionalKey(target) {
  return Object.keys(target).some(isPositionalKey);
};

var setAndResetVelocity = function setAndResetVelocity(value, to) {
  // Looks odd but setting it twice doesn't render, it'll just
  // set both prev and current to the latest value
  value.set(to, false);
  value.set(to);
};

var isNumOrPxType = function isNumOrPxType(v) {
  return v === number || v === px;
};

var BoundingBoxDimension;

(function (BoundingBoxDimension) {
  BoundingBoxDimension["width"] = "width";
  BoundingBoxDimension["height"] = "height";
  BoundingBoxDimension["left"] = "left";
  BoundingBoxDimension["right"] = "right";
  BoundingBoxDimension["top"] = "top";
  BoundingBoxDimension["bottom"] = "bottom";
})(BoundingBoxDimension || (BoundingBoxDimension = {}));

var getPosFromMatrix = function getPosFromMatrix(matrix, pos) {
  return parseFloat(matrix.split(", ")[pos]);
};

var getTranslateFromMatrix = function getTranslateFromMatrix(pos2, pos3) {
  return function (_bbox, _a) {
    var transform = _a.transform;
    if (transform === "none" || !transform) return 0;
    var matrix3d = transform.match(/^matrix3d\((.+)\)$/);

    if (matrix3d) {
      return getPosFromMatrix(matrix3d[1], pos3);
    } else {
      var matrix = transform.match(/^matrix\((.+)\)$/);

      if (matrix) {
        return getPosFromMatrix(matrix[1], pos2);
      } else {
        return 0;
      }
    }
  };
};

var transformKeys = new Set(["x", "y", "z"]);
var nonTranslationalTransformKeys = transformProps.filter(function (key) {
  return !transformKeys.has(key);
});

function removeNonTranslationalTransform(values, nativeElement) {
  var removedTransforms = [];
  nonTranslationalTransformKeys.forEach(function (key) {
    var value = values.get(key);

    if (value !== undefined) {
      removedTransforms.push([key, value.get()]);
      value.set(key.startsWith("scale") ? 1 : 0);
    }
  }); // Apply changes to element before measurement

  if (removedTransforms.length) nativeElement.render();
  return removedTransforms;
}

var positionalValues = {
  // Dimensions
  width: function width(_a) {
    var width = _a.width;
    return width;
  },
  height: function height(_a) {
    var height = _a.height;
    return height;
  },
  top: function top(_bbox, _a) {
    var top = _a.top;
    return parseFloat(top);
  },
  left: function left(_bbox, _a) {
    var left = _a.left;
    return parseFloat(left);
  },
  bottom: function bottom(_a, _b) {
    var height = _a.height;
    var top = _b.top;
    return parseFloat(top) + height;
  },
  right: function right(_a, _b) {
    var width = _a.width;
    var left = _b.left;
    return parseFloat(left) + width;
  },
  // Transform
  x: getTranslateFromMatrix(4, 13),
  y: getTranslateFromMatrix(5, 14)
};

var convertChangedValueTypes = function convertChangedValueTypes(target, values, nativeElement, changedKeys) {
  var originBbox = nativeElement.getBoundingBox();
  var elementComputedStyle = nativeElement.getComputedStyle();
  var display = elementComputedStyle.display,
      top = elementComputedStyle.top,
      left = elementComputedStyle.left,
      bottom = elementComputedStyle.bottom,
      right = elementComputedStyle.right,
      transform = elementComputedStyle.transform;
  var originComputedStyle = {
    top: top,
    left: left,
    bottom: bottom,
    right: right,
    transform: transform
  }; // If the element is currently set to display: "none", make it visible before
  // measuring the target bounding box

  if (display === "none") {
    nativeElement.setStyle("display", target.display || "block");
  } // Apply the latest values (as set in checkAndConvertChangedValueTypes)


  nativeElement.render();
  var targetBbox = nativeElement.getBoundingBox();
  changedKeys.forEach(function (key) {
    // Restore styles to their **calculated computed style**, not their actual
    // originally set style. This allows us to animate between equivalent pixel units.
    var value = values.get(key);
    setAndResetVelocity(value, positionalValues[key](originBbox, originComputedStyle));
    target[key] = positionalValues[key](targetBbox, elementComputedStyle);
  });
  return target;
};

var checkAndConvertChangedValueTypes = function checkAndConvertChangedValueTypes(values, nativeElement, target, transitionEnd) {
  if (transitionEnd === void 0) {
    transitionEnd = {};
  }

  target = __assign({}, target);
  transitionEnd = __assign({}, transitionEnd);
  var targetPositionalKeys = Object.keys(target).filter(isPositionalKey); // We want to remove any transform values that could affect the element's bounding box before
  // it's measured. We'll reapply these later.

  var removedTransformValues = [];
  var hasAttemptedToRemoveTransformValues = false;
  var changedValueTypeKeys = targetPositionalKeys.reduce(function (acc, key) {
    var value = values.get(key);
    if (!value) return acc;
    var from = value.get();
    var to = target[key];
    var fromType = getDimensionValueType(from);
    var toType; // TODO: The current implementation of this basically throws an error
    // if you try and do value conversion via keyframes. There's probably
    // a way of doing this but the performance implications would need greater scrutiny,
    // as it'd be doing multiple resize-remeasure operations.

    if (isKeyframesTarget(to)) {
      var numKeyframes = to.length;

      for (var i = to[0] === null ? 1 : 0; i < numKeyframes; i++) {
        if (!toType) {
          toType = getDimensionValueType(to[i]);
          invariant(toType === fromType || isNumOrPxType(fromType) && isNumOrPxType(toType), "Keyframes must be of the same dimension as the current value");
        } else {
          invariant(getDimensionValueType(to[i]) === toType, "All keyframes must be of the same type");
        }
      }
    } else {
      toType = getDimensionValueType(to);
    }

    if (fromType !== toType) {
      // If they're both just number or px, convert them both to numbers rather than
      // relying on resize/remeasure to convert (which is wasteful in this situation)
      if (isNumOrPxType(fromType) && isNumOrPxType(toType)) {
        var current = value.get();

        if (typeof current === "string") {
          value.set(parseFloat(current));
        }

        if (typeof to === "string") {
          target[key] = parseFloat(to);
        } else if (Array.isArray(to) && toType === px) {
          target[key] = to.map(parseFloat);
        }
      } else {
        // If we're going to do value conversion via DOM measurements, we first
        // need to remove non-positional transform values that could affect the bbox measurements.
        if (!hasAttemptedToRemoveTransformValues) {
          removedTransformValues = removeNonTranslationalTransform(values, nativeElement);
          hasAttemptedToRemoveTransformValues = true;
        }

        acc.push(key);
        transitionEnd[key] = transitionEnd[key] !== undefined ? transitionEnd[key] : target[key];
        setAndResetVelocity(value, to);
      }
    }

    return acc;
  }, []);

  if (changedValueTypeKeys.length) {
    var convertedTarget = convertChangedValueTypes(target, values, nativeElement, changedValueTypeKeys); // If we removed transform values, reapply them before the next render

    if (removedTransformValues.length) {
      removedTransformValues.forEach(function (_a) {
        var key = _a[0],
            value = _a[1];
        values.get(key).set(value);
      });
    } // Reapply original values


    nativeElement.render();
    return {
      target: convertedTarget,
      transitionEnd: transitionEnd
    };
  } else {
    return {
      target: target,
      transitionEnd: transitionEnd
    };
  }
};
/**
 * Convert value types for x/y/width/height/top/left/bottom/right
 *
 * Allows animation between `'auto'` -> `'100%'` or `0` -> `'calc(50% - 10vw)'`
 *
 * @param values
 * @param nativeElement
 * @param target
 * @param transitionEnd
 * @internal
 */


function unitConversion(values, nativeElement, target, transitionEnd) {
  return hasPositionalKey(target) ? checkAndConvertChangedValueTypes(values, nativeElement, target, transitionEnd) : {
    target: target,
    transitionEnd: transitionEnd
  };
}

var parseDomVariant = function parseDomVariant(values, nativeElement) {
  return function (target, transitionEnd) {
    var resolved = resolveCSSVariables(values, nativeElement, target, transitionEnd);
    target = resolved.target;
    transitionEnd = resolved.transitionEnd;
    return unitConversion(values, nativeElement, target, transitionEnd);
  };
};
/**
 * @internal
 */


var SharedLayoutContext = createContext(batchTransitions());

var singleBorderRadius = function singleBorderRadius(key) {
  return {
    reset: function reset(style) {
      return style.borderRadius !== undefined ? style.borderRadius : "";
    },
    read: function read(radius) {
      return radius ? parseFloat(radius) : 0;
    },
    createUpdater: function createUpdater(values, origin, target, current, delta, treeScale) {
      if (!origin && !target) return;
      var motionValue = values.get(key, "");
      return function (p) {
        var v = mix(origin, target, p);
        current[key] = v;
        var targetX = v / delta.x.scale / treeScale.x;
        var targetY = v / delta.y.scale / treeScale.y;
        motionValue.set(targetX + "px " + targetY + "px");
      };
    }
  };
};

var defaultMagicValues = {
  backgroundColor: {
    reset: function reset(style) {
      return style.background !== undefined ? style.background : "";
    }
  },
  borderRadius: {
    read: false
  },
  borderTopLeftRadius: singleBorderRadius("borderTopLeftRadius"),
  borderTopRightRadius: singleBorderRadius("borderTopRightRadius"),
  borderBottomLeftRadius: singleBorderRadius("borderBottomLeftRadius"),
  borderBottomRightRadius: singleBorderRadius("borderBottomRightRadius"),
  boxShadow: {
    createUpdater: function createUpdater(values, origin, target, current, delta, treeScale) {
      if (isEmptyBoxShadow(origin) && isEmptyBoxShadow(target)) {
        return;
      }

      var originShadow = getAnimatableShadow(origin, target);
      var targetShadow = getAnimatableShadow(target, origin);

      var currentShadow = __spreadArrays(originShadow);

      var mixShadowColor = mixColor(originShadow[0], currentShadow[0]);
      var shadowTemplate = complex.createTransformer(target !== "none" ? target : origin);
      var dx = delta.x;
      var dy = delta.y;
      var boxShadow = values.get("boxShadow", "");
      return function (p) {
        // Update box shadow
        currentShadow[0] = mixShadowColor(p); // color

        currentShadow[1] = mix(originShadow[1], targetShadow[1], p); // x

        currentShadow[2] = mix(originShadow[2], targetShadow[2], p); // y

        currentShadow[3] = mix(originShadow[3], targetShadow[3], p); // blur

        currentShadow[4] = mix(originShadow[4], targetShadow[4], p); // spread
        // Update prev box shadow before FLIPPing

        current.boxShadow = shadowTemplate(currentShadow); // Apply FLIP inversion to physical dimensions. We need to take an average scale for XY to apply
        // to blur and spread, which affect both axis equally.

        currentShadow[1] = currentShadow[1] / dx.scale / treeScale.x;
        currentShadow[2] = currentShadow[2] / dy.scale / treeScale.y;
        var averageXYScale = mix(dx.scale, dy.scale, 0.5);
        var averageTreeXTScale = mix(treeScale.x, treeScale.y, 0.5);
        currentShadow[3] = currentShadow[3] / averageXYScale / averageTreeXTScale; // blur

        currentShadow[4] = currentShadow[4] / averageXYScale / averageTreeXTScale; // spread

        boxShadow.set(shadowTemplate(currentShadow));
      };
    }
  },
  color: {},
  opacity: {
    read: function read(opacity) {
      return opacity !== null ? parseFloat(opacity) : 0;
    }
  },
  position: {}
};

function getAnimatableShadow(shadow, fallback) {
  if (shadow === "none") {
    shadow = complex.getAnimatableNone(fallback);
  }

  return complex.parse(shadow);
}

function isEmptyBoxShadow(shadow) {
  return !shadow || shadow === "none";
}
/**
 * Magic Motion relies on multiple components and class components only support, hence this
 * wrapper component that provides those contexts as props.
 */


var SharedLayoutContextProvider = function SharedLayoutContextProvider(props) {
  var _a = usePresence(),
      isPresent = _a[0],
      safeToRemove = _a[1];

  var magicContext = useContext(SharedLayoutContext);

  var _b = useContext(MotionPluginContext),
      magicValues = _b.magicValues,
      transformPagePoint = _b.transformPagePoint;

  return createElement(Magic, __assign({}, props, {
    isPresent: isPresent,
    safeToRemove: safeToRemove,
    magicContext: magicContext,
    magicValues: magicValues,
    transformPagePoint: transformPagePoint
  }));
};

var Magic =
/** @class */
function (_super) {
  __extends(Magic, _super);

  function Magic(props) {
    var _this = _super.call(this, props) || this;

    _this.shouldTransition = true;
    _this.shouldResumeFromPrevious = false;
    _this.shouldRestoreVisibility = false;
    _this.isVisible = true;
    _this.hasAnimatedRotate = false;
    _this.correctedLayout = {
      x: {
        min: 0,
        max: 0
      },
      y: {
        min: 0,
        max: 0
      }
    };
    _this.treeScale = {
      x: 1,
      y: 1
    };
    _this.current = {
      rotate: 0
    };
    _this.prevRotate = 0;
    _this.delta = props.localContext.magicDelta;
    _this.depth = props.localContext.magicDepth;
    _this.progress = props.localContext.magicProgress;
    var magicValues = props.magicValues;
    _this.supportedMagicValues = __assign(__assign({}, defaultMagicValues), magicValues);
    _this.animatableStyles = [];

    for (var key in _this.supportedMagicValues) {
      if (!_this.supportedMagicValues[key].createUpdater) {
        _this.animatableStyles.push(key);
      }
    }

    return _this;
  }

  Magic.prototype.componentDidMount = function () {
    var _this = this;

    var magicContext = this.props.magicContext;

    if (isControlledTree(magicContext)) {
      this.unregisterFromSharedLayoutContext = magicContext.register(this);
    } else {
      this.getSnapshotBeforeUpdate = function () {
        _this.snapshotOrigin();

        magicContext.add(_this);
        return null;
      };

      this.componentDidUpdate = function () {
        return magicContext.flush();
      };
    }
  };

  Magic.prototype.componentWillUnmount = function () {
    this.unregisterFromSharedLayoutContext && this.unregisterFromSharedLayoutContext();
    this.stopLayoutAnimation && this.stopLayoutAnimation();
  }; // TODO Can we combine this with MagicMotion?


  Magic.prototype.shouldComponentUpdate = function (nextProps) {
    var hasDependency = this.props.magicDependency !== undefined || nextProps.magicDependency !== undefined;
    var dependencyHasChanged = this.props.magicDependency !== nextProps.magicDependency;
    var presenceHasChanged = this.props.isPresent !== nextProps.isPresent;
    this.shouldTransition = !hasDependency || hasDependency && dependencyHasChanged || presenceHasChanged;
    return true;
  }; // TODO: Find a way to abstract this, as it's only needed in Framer


  Magic.prototype.resetRotation = function () {
    var _a = this.props,
        nativeElement = _a.nativeElement,
        values = _a.values;
    var rotate = values.get("rotate");
    this.current.rotate = rotate ? rotate.get() : 0;
    if (!this.current.rotate) return;
    nativeElement.setStyle("rotate", 0);
    nativeElement.render();
  };

  Magic.prototype.resetStyles = function () {
    var _a = this.props,
        animate = _a.animate,
        nativeElement = _a.nativeElement,
        _b = _a.style,
        style = _b === void 0 ? {} : _b;
    var reset = resetStyles(style, this.supportedMagicValues); // If we're animating opacity separately, we don't want to reset
    // as it causes a visual flicker when adding the component
    // TODO: We should do this universally for all animating props
    // and account for variants too.

    if (typeof animate === "object" && animate.hasOwnProperty("opacity")) {
      delete reset.opacity;
    }

    nativeElement.setStyle(reset);
    nativeElement.render();
  };

  Magic.prototype.snapshotOrigin = function () {
    var _a = this.props,
        nativeElement = _a.nativeElement,
        transformPagePoint = _a.transformPagePoint;
    var origin = snapshot(nativeElement, this.supportedMagicValues, transformPagePoint);
    applyCurrent(origin.style, this.current);
    this.measuredOrigin = origin;
  };

  Magic.prototype.snapshotTarget = function () {
    var _a = this.props,
        nativeElement = _a.nativeElement,
        style = _a.style,
        transformPagePoint = _a.transformPagePoint;
    var target = snapshot(nativeElement, this.supportedMagicValues, transformPagePoint);
    target.style.rotate = resolve(0, style && style.rotate);
    this.measuredTarget = target;
  };

  Magic.prototype.hide = function (apply) {
    if (apply === void 0) {
      apply = false;
    }

    this.isVisible = false;

    if (apply) {
      var values = this.props.values;
      var opacity = values.get("opacity", 0);
      opacity.set(0);
    }
  };

  Magic.prototype.show = function (apply) {
    if (apply === void 0) {
      apply = false;
    }

    this.isVisible = true;

    if (apply) {
      var _a = this.props,
          values = _a.values,
          style = _a.style;
      var opacity = values.get("opacity", 1);
      var newOpacity = style ? resolve(1, style.opacity) : 1;
      opacity.set(newOpacity);
    } else {
      this.shouldRestoreVisibility = true;
    }
  }; // TODO: Reduce safeToRemove calls


  Magic.prototype.startAnimation = function (_a) {
    var _this = this;

    if (_a === void 0) {
      _a = {};
    }

    var origin = _a.origin,
        target = _a.target,
        opts = __rest(_a, ["origin", "target"]);

    var _b = this.props,
        nativeElement = _b.nativeElement,
        values = _b.values;
    var rotate = values.get("rotate");
    rotate && nativeElement.setStyle("rotate", rotate.get());

    if (!this.shouldTransition) {
      this.safeToRemove();
      return;
    }

    syncRenderSession.open();
    this.visualTarget = target || this.measuredTarget;
    this.visualOrigin = origin || this.measuredOrigin || this.visualTarget;

    if (this.visualOrigin && this.visualTarget) {
      var animations = [this.startLayoutAnimation(opts), this.startStyleAnimation(opts)].filter(Boolean);
      if (!animations.length) this.safeToRemove();
      Promise.all(animations).then(function () {
        var onMagicComplete = _this.props.onMagicComplete;
        onMagicComplete && onMagicComplete();
      });
    } else {
      this.safeToRemove();
    }

    syncRenderSession.flush();
  };
  /**
   * This uses the FLIP animation technique to animate physical dimensions
   * and correct distortion on related styles (ie borderRadius etc)
   */


  Magic.prototype.startLayoutAnimation = function (opts) {
    var _this = this;

    var animation;
    this.stopLayoutAnimation && this.stopLayoutAnimation();
    var originStyle = this.visualOrigin.style;
    var targetStyle = this.visualTarget.style;
    var isAnimatingRotate = Boolean(originStyle.rotate || targetStyle.rotate); // We really want to know if its ever animated rotate and the above isn't good enough

    if (isAnimatingRotate) this.hasAnimatedRotate = isAnimatingRotate;
    var values = this.props.values;
    var updaters = {};

    for (var key in this.supportedMagicValues) {
      var handler = this.supportedMagicValues[key];
      if (!handler.createUpdater) continue;
      updaters[key] = handler.createUpdater(values, originStyle[key], targetStyle[key], this.current, this.delta, this.treeScale);
    }

    this.target = {
      x: {
        min: 0,
        max: 0
      },
      y: {
        min: 0,
        max: 0
      }
    };
    var x = values.get("x", 0);
    var y = values.get("y", 0);
    var scaleX = values.get("scaleX", 1);
    var scaleY = values.get("scaleY", 1);
    var rotate = values.get("rotate", 0);
    var opacity = values.get("opacity", this.visualOrigin.style.opacity);

    var frame = function frame() {
      // TODO: Break up each of these so we can animate separately
      var p = _this.progress.get() / 1000;

      _this.updateBoundingBox(p, _this.hasAnimatedRotate ? 0.5 : undefined);

      _this.updateTransform(x, y, scaleX, scaleY);

      _this.hasAnimatedRotate && _this.updateRotate(p, rotate);

      for (var key in updaters) {
        var updater = updaters[key];
        updater && updater(p);
      }

      if (opts.crossfadeEasing) {
        opacity.set(mix(originStyle.opacity, targetStyle.opacity, opts.crossfadeEasing(p)));
      }
    };

    var progressOrigin = 0;
    var progressTarget = 1000;
    this.progress.set(progressOrigin);
    this.progress.set(progressOrigin); // Set twice to hard-reset velocity

    var _a = this.props,
        transition = _a.transition,
        animate = _a.animate;

    if (animate !== false) {
      animation = startAnimation("progress", this.progress, progressTarget, __assign(__assign({}, opts.transition || transition), {
        restDelta: 1,
        restSpeed: 10
      })).then(function () {
        return _this.safeToRemove();
      });
    } else {
      this.safeToRemove();
    } // TODO: We're currently chaining just the parent and child deep, and if both
    // update then `frame` fires twice in a frame. This only leads to one render
    // but it'd be cooler if it batched updates


    var parentContext = this.props.parentContext;
    var magicProgress = parentContext.magicProgress;
    var unsubscribeProgress = this.progress.onChange(frame);
    var unsubscribeParentProgress;

    if (magicProgress) {
      unsubscribeParentProgress = magicProgress.onChange(frame);
    }

    this.stopLayoutAnimation = function () {
      _this.progress.stop();

      unsubscribeProgress();
      unsubscribeParentProgress && unsubscribeParentProgress();
    };

    frame();
    return animation;
  };
  /**
   * This is a straight animation between prev/next styles. This animates
   * styles that don't need scale inversion correction.
   */


  Magic.prototype.startStyleAnimation = function (opts) {
    var shouldTransitionStyle = false;
    var target = {};
    var values = this.props.values;
    var numAnimatableStyles = this.animatableStyles.length;

    for (var i = 0; i < numAnimatableStyles; i++) {
      var key = this.animatableStyles[i];
      if (key === "opacity" && opts.crossfadeEasing) continue;
      var originStyle = this.visualOrigin.style[key];
      var nextStyle = this.visualTarget.style[key];

      if (originStyle !== nextStyle) {
        shouldTransitionStyle = true;
        var value = values.get(key, originStyle);
        value.set(originStyle);
        target[key] = nextStyle;
      }
    }

    var _a = this.props,
        transition = _a.transition,
        controls = _a.controls;
    target.transition = opts.transition || transition || {};

    if (opts.crossfadeEasing) {
      target.transition = {
        opacity: __assign(__assign({}, target.transition), {
          type: "tween",
          ease: opts.crossfadeEasing
        }),
        "default": __assign({}, target.transition)
      };
    }

    if (shouldTransitionStyle) {
      return controls.start(target);
    }
  };

  Magic.prototype.updateBoundingBox = function (p, origin) {
    var parentContext = this.props.parentContext;
    var parentDeltas = parentContext.magicDeltas || [];
    resetLayout(this.correctedLayout, this.measuredTarget.layout);
    applyTreeDeltas(this.correctedLayout, parentDeltas);
    easeBox(this.target, this.visualOrigin.layout, this.visualTarget.layout, p);
    calcBoxDelta(this.delta, this.target, this.correctedLayout, origin); // TODO: If we could return this from applyTreeDeltas
    // it'd save a second loop

    calcTreeScale(this.treeScale, parentDeltas);
  };

  Magic.prototype.updateTransform = function (x, y, scaleX, scaleY) {
    var nativeElement = this.props.nativeElement;
    var dx = this.delta.x;
    var dy = this.delta.y;
    nativeElement.setStyle("originX", dx.origin);
    nativeElement.setStyle("originY", dy.origin);
    x.set(dx.translate / this.treeScale.x);
    y.set(dy.translate / this.treeScale.y);
    scaleX.set(dx.scale);
    scaleY.set(dy.scale);
  };

  Magic.prototype.updateRotate = function (p, rotate) {
    var target = mix(this.visualOrigin.style.rotate, this.visualTarget.style.rotate, p);
    rotate.set(target);
  };

  Magic.prototype.isPresent = function () {
    return this.props.isPresent;
  };

  Magic.prototype.safeToRemove = function () {
    var safeToRemove = this.props.safeToRemove;
    safeToRemove && safeToRemove();
  };

  Magic.prototype.render = function () {
    return null;
  };

  return Magic;
}(Component);

function isControlledTree(context) {
  return !!context.register;
}

function resetAxis(axis, originAxis) {
  axis.min = originAxis.min;
  axis.max = originAxis.max;
}

function resetLayout(box, originBox) {
  resetAxis(box.x, originBox.x);
  resetAxis(box.y, originBox.y);
}

var magic = {
  key: "magic",
  shouldRender: function shouldRender(_a) {
    var animate = _a.animate,
        layoutId = _a.layoutId;
    var hasMagicProps = typeof animate === "boolean" || layoutId !== undefined;
    return hasMagicProps && typeof window !== "undefined";
  },
  Component: SharedLayoutContextProvider
};
/**
 * A list of all valid MotionProps.
 *
 * @internalremarks
 * This doesn't throw if a `MotionProp` name is missing - it should.
 */

var validMotionProps = new Set(["initial", "animate", "exit", "style", "variants", "transition", "transformTemplate", "transformValues", "custom", "inherit", "static", "layoutId", "magicDependency", "allowTransformNone", "onMagicComplete", "onAnimationStart", "onAnimationComplete", "onUpdate", "onDragStart", "onDrag", "onDragEnd", "onDirectionLock", "onDragTransitionEnd", "drag", "dragControls", "dragListener", "dragConstraints", "dragDirectionLock", "dragElastic", "dragMomentum", "dragPropagation", "dragTransition", "_dragValueX", "_dragValueY", "_dragTransitionControls", "dragOriginX", "dragOriginY", "onPan", "onPanStart", "onPanEnd", "onPanSessionStart", "onTap", "onTapStart", "onTapCancel", "whileHover", "whileTap", "onHoverEnd", "onHoverStart"]);
/**
 * Check whether a prop name is a valid `MotionProp` key.
 *
 * @param key - Name of the property to check
 * @returns `true` is key is a valid `MotionProp`.
 *
 * @public
 */

function isValidMotionProp(key) {
  return validMotionProps.has(key);
}

var AnimatePropType;

(function (AnimatePropType) {
  AnimatePropType["Target"] = "Target";
  AnimatePropType["VariantLabel"] = "VariantLabel";
  AnimatePropType["AnimationSubscription"] = "AnimationSubscription";
})(AnimatePropType || (AnimatePropType = {}));

function shallowCompare(next, prev) {
  if (prev === null) return false;
  var prevLength = prev.length;
  if (prevLength !== next.length) return false;

  for (var i = 0; i < prevLength; i++) {
    if (prev[i] !== next[i]) return false;
  }

  return true;
}

var hasUpdated = function hasUpdated(prev, next) {
  return next !== undefined && (Array.isArray(prev) && Array.isArray(next) ? !shallowCompare(next, prev) : prev !== next);
};

function targetWithoutTransition(_a, mergeTransitionEnd) {
  if (mergeTransitionEnd === void 0) {
    mergeTransitionEnd = false;
  }

  var transition = _a.transition,
      transitionEnd = _a.transitionEnd,
      target = __rest(_a, ["transition", "transitionEnd"]);

  return mergeTransitionEnd ? __assign(__assign({}, target), transitionEnd) : target;
}
/**
 * Handle the `animate` prop when its an object of values, ie:
 *
 * ```jsx
 * <motion.div animate={{ opacity: 1 }} />
 * ```
 *
 * @internalremarks
 * It might be worth consolidating this with `use-variants`
 *
 * ```jsx
 * <motion.div animate="visible" />
 * ```
 *
 * @param target
 * @param controls
 * @param values
 * @param transition
 *
 * @internal
 */


function useAnimateProp(targetAndTransition, controls, values, defaultTransition) {
  var isInitialRender = useRef(true);
  var prevValues = useRef(null);

  if (!prevValues.current) {
    prevValues.current = targetWithoutTransition(targetAndTransition, true);
  }

  useEffect(function () {
    var targetToAnimate = {}; // These are the values we're actually animating

    var animatingTarget = targetWithoutTransition(targetAndTransition); // This is the target as it'll be once transitionEnd values are applied

    var finalTarget = targetWithoutTransition(targetAndTransition, true); // Detect which values have changed between renders

    for (var key in animatingTarget) {
      // This value should animate on mount if this value doesn't already exist (wasn't
      // defined in `style` or `initial`) or if it does exist and it's already changed.
      var shouldAnimateOnMount = isInitialRender.current && (!values.has(key) || values.get(key).get() !== finalTarget[key]); // If this value has updated between renders or it's we're animating this value on mount,
      // add it to the animate target.

      var isValidValue = finalTarget[key] !== null;
      var valueHasUpdated = hasUpdated(prevValues.current[key], finalTarget[key]);

      if (isValidValue && (valueHasUpdated || shouldAnimateOnMount)) {
        targetToAnimate[key] = animatingTarget[key];
      }
    }

    isInitialRender.current = false;
    prevValues.current = __assign(__assign({}, prevValues.current), finalTarget);

    if (Object.keys(targetToAnimate).length) {
      controls.start(__assign(__assign({}, targetToAnimate), {
        transition: targetAndTransition.transition || defaultTransition,
        transitionEnd: targetAndTransition.transitionEnd
      }));
    }
  }, [targetAndTransition]);
}

var labelsToArray = function labelsToArray(label) {
  if (!label) {
    return [];
  }

  if (Array.isArray(label)) {
    return label;
  }

  return [label];
};

var resolveVariantLabels = function resolveVariantLabels(variant) {
  var unresolvedVariant = variant instanceof MotionValue ? variant.get() : variant;
  return Array.from(new Set(labelsToArray(unresolvedVariant)));
};
/**
 * Hooks in React sometimes accept a dependency array as their final argument. (ie useEffect/useMemo)
 * When values in this array change, React re-runs the dependency. However if the array
 * contains a variable number of items, React throws an error.
 */


var asDependencyList = function asDependencyList(list) {
  return [list.join(",")];
};

var hasVariantChanged = function hasVariantChanged(oldVariant, newVariant) {
  return oldVariant.join(",") !== newVariant.join(",");
};
/**
 * Handle variants and the `animate` prop when its set as variant labels.
 *
 * @param initial - Initial variant(s)
 * @param animate - Variant(s) to animate to
 * @param inherit - `true` is inheriting animations from parent
 * @param controls - Animation controls
 *
 * @internal
 */


function useVariants(initial, animate, inherit, controls) {
  var targetVariants = resolveVariantLabels(animate);
  var context = useContext(MotionContext);
  var parentAlreadyMounted = context.hasMounted && context.hasMounted.current;
  var hasMounted = useRef(false);
  useEffect(function () {
    var shouldAnimate = false;

    if (inherit) {
      // If we're inheriting variant changes and the parent has already
      // mounted when this component loads, we need to manually trigger
      // this animation.
      shouldAnimate = !!parentAlreadyMounted;
      targetVariants = resolveVariantLabels(context.animate);
    } else {
      shouldAnimate = hasMounted.current || hasVariantChanged(resolveVariantLabels(initial), targetVariants);
    }

    shouldAnimate && controls.start(targetVariants);
    hasMounted.current = true;
  }, asDependencyList(targetVariants));
}
/**
 * `useAnimationGroupSubscription` allows a component to subscribe to an
 * externally-created `AnimationControls`, created by the `useAnimation` hook.
 *
 * @param animation
 * @param controls
 *
 * @internal
 */


function useAnimationGroupSubscription(animation, controls) {
  var unsubscribe = useMemo(function () {
    return animation.subscribe(controls);
  }, [animation]);
  useEffect(function () {
    return function () {
      unsubscribe && unsubscribe();
    };
  }, [unsubscribe]);
}

var _a, _b;

var AnimatePropComponents = (_a = {}, _a[AnimatePropType.Target] = makeRenderlessComponent(function (_a) {
  var animate = _a.animate,
      controls = _a.controls,
      values = _a.values,
      transition = _a.transition;
  return useAnimateProp(animate, controls, values, transition);
}), _a[AnimatePropType.VariantLabel] = makeRenderlessComponent(function (_a) {
  var animate = _a.animate,
      _b = _a.inherit,
      inherit = _b === void 0 ? true : _b,
      controls = _a.controls,
      initial = _a.initial;
  return useVariants(initial, animate, inherit, controls);
}), _a[AnimatePropType.AnimationSubscription] = makeRenderlessComponent(function (_a) {
  var animate = _a.animate,
      controls = _a.controls;
  return useAnimationGroupSubscription(animate, controls);
}), _a);

var isVariantLabel$1 = function isVariantLabel$1(prop) {
  return Array.isArray(prop) || typeof prop === "string";
};

var isAnimationSubscription = function isAnimationSubscription(_a) {
  var animate = _a.animate;
  return animate instanceof AnimationControls;
};

var animationProps = ["initial", "animate", "whileTap", "whileHover"];
var animatePropTypeTests = (_b = {}, _b[AnimatePropType.Target] = function (props) {
  return props.animate !== undefined && !isVariantLabel$1(props.animate) && !isAnimationSubscription(props);
}, _b[AnimatePropType.VariantLabel] = function (props) {
  return props.variants !== undefined || animationProps.some(function (key) {
    return typeof props[key] === "string";
  });
}, _b[AnimatePropType.AnimationSubscription] = isAnimationSubscription, _b);

var getAnimationComponent = function getAnimationComponent(props) {
  var animatePropType = undefined;

  for (var key in AnimatePropType) {
    if (animatePropTypeTests[key](props)) {
      animatePropType = key;
    }
  }

  return animatePropType ? AnimatePropComponents[animatePropType] : undefined;
};

var Exit = {
  key: "exit",
  shouldRender: function shouldRender(props) {
    return !!props.exit && !checkShouldInheritVariant(props);
  },
  Component: makeRenderlessComponent(function (props) {
    var _a;

    var animate = props.animate,
        controls = props.controls,
        exit = props.exit;

    var _b = usePresence(),
        isPresent = _b[0],
        onExitComplete = _b[1];

    var presenceContext = useContext(PresenceContext);
    var isPlayingExitAnimation = useRef(false);
    var custom = ((_a = presenceContext) === null || _a === void 0 ? void 0 : _a.custom) !== undefined ? presenceContext.custom : props.custom;
    useEffect(function () {
      if (!isPresent) {
        if (!isPlayingExitAnimation.current && exit) {
          controls.setProps(__assign(__assign({}, props), {
            custom: custom
          }));
          controls.start(exit).then(onExitComplete);
        }

        isPlayingExitAnimation.current = true;
      } else if (isPlayingExitAnimation.current && animate && typeof animate !== "boolean" && !(animate instanceof AnimationControls)) {
        controls.start(animate);
      }

      if (isPresent) {
        isPlayingExitAnimation.current = false;
      }
    }, [animate, controls, custom, exit, isPresent, onExitComplete, props]);
  })
};

var isPropValid = function isPropValid(key) {
  return !isValidMotionProp(key);
};
/**
 * Emotion and Styled Components both allow users to pass through arbitrary props to their components
 * to dynamically generate CSS. They both use the `@emotion/is-prop-valid` package to determine which
 * of these should be passed to the underlying DOM node.
 *
 * However, when styling a Motion component `styled(motion.div)`, both packages pass through *all* props
 * as it's seen as an arbitrary component rather than a DOM node. Motion only allows arbitrary props
 * passed through the `custom` prop so it doesn't *need* the payload or computational overhead of
 * `@emotion/is-prop-valid`, however to fix this problem we need to use it.
 *
 * By making it an optionalDependency we can offer this functionality only in the situations where it's
 * actually required.
 */


try {
  var emotionIsPropValid_1 = require("@emotion/is-prop-valid")["default"];

  isPropValid = function isPropValid(key) {
    // Handle events explicitly as Emotion validates them all as true
    if (key.startsWith("on")) {
      return !isValidMotionProp(key);
    } else {
      return emotionIsPropValid_1(key);
    }
  };
} catch (_a) {// We don't need to actually do anything here - the fallback is the existing `isPropValid`.
}

function filterValidProps(props) {
  var domProps = {};

  for (var key in props) {
    if (isPropValid(key)) {
      domProps[key] = props[key];
    }
  }

  return domProps;
}

var buildHTMLProps = function buildHTMLProps(values, style, isStatic, isDrag) {
  // The `any` isn't ideal but it is the type of createElement props argument
  var props = {
    style: buildStyleAttr(values, style, isStatic)
  };

  if (isDrag) {
    // Disable text selection
    props.style.userSelect = "none"; // Disable the ghost element when a user drags

    props.draggable = false;
  }

  return props;
};

var buildSVGProps = function buildSVGProps(values, style) {
  var motionValueStyles = resolveCurrent(values);
  var props = buildSVGAttrs(motionValueStyles, undefined, undefined, undefined, undefined, false);
  props.style = __assign(__assign({}, style), props.style);
  return props;
};

var defaultFeatures = [magic, Drag, Gestures, Exit];
/**
 * Create a configuration for `motion` components that provides DOM-specific feature.
 *
 * @internal
 */

function createDomMotionConfig(ComponentToRender) {
  var isDOM = typeof ComponentToRender === "string";
  var isSVG = isDOM && svgElements.indexOf(ComponentToRender) !== -1;
  return {
    renderComponent: function renderComponent(nativeElement, style, values, props, isStatic) {
      var forwardedProps = isDOM ? filterValidProps(props) : props;
      var staticVisualStyles = isSVG ? buildSVGProps(values, style) : buildHTMLProps(values, style, isStatic, !!props.drag);
      return createElement(ComponentToRender, __assign(__assign(__assign({}, forwardedProps), {
        ref: nativeElement.ref
      }), staticVisualStyles));
    },

    /**
     * loadFeatures gets used by the `motion` component
     *
     * Each feature component gets provided the `ref`, animation controls and the `MotionValuesMap`
     * generated for that component, as well as all the `props` passed to it by the user.
     *
     * The pattern used to determine whether to load and use each piece of feature is
     * consistent (should render? Then push component) and could be used to extend feature.
     *
     * By exposing a mutable piece of memory via an API like `extendMotionComponent` we could
     * allow users to add `MotionFeature`s. This would allow us to offer file size
     * reductions by shipping an entry point that doesn't load gesture and drag functionality, and
     * also offer a way for users to develop plugins/other feature. Because these functionalities
     * are loaded as components, we can look into using Suspense for this purpose.
     *
     * For user-defined feature we'd need to allow
     *  1) User-defined prop typing (extending `P`)
     *  2) User-defined "clean props" function that removes their plugin's props before being passed to the DOM.
     */
    loadFeatures: function loadFeatures(nativeElement, values, props, context, parentContext, controls, inherit, plugins) {
      var allFeatures = __spreadArrays(defaultFeatures, plugins.features);

      var numFeatures = allFeatures.length;
      var activeComponents = []; // TODO: Consolidate Animation feature loading strategy with other functionality components

      var Animation = getAnimationComponent(props);

      if (Animation) {
        activeComponents.push(createElement(Animation, {
          key: "animation",
          initial: props.initial,
          animate: props.animate,
          variants: props.variants,
          transition: props.transition,
          controls: controls,
          inherit: inherit,
          values: values
        }));
      }

      for (var i = 0; i < numFeatures; i++) {
        var _a = allFeatures[i],
            shouldRender = _a.shouldRender,
            key = _a.key,
            Component = _a.Component;

        if (shouldRender(props, parentContext)) {
          activeComponents.push(createElement(Component, __assign({
            key: key
          }, props, {
            localContext: context,
            parentContext: parentContext,
            values: values,
            controls: controls,
            nativeElement: nativeElement
          })));
        }
      }

      return activeComponents;
    },
    getValueControlsConfig: function getValueControlsConfig(nativeElement, values) {
      return {
        values: values,
        readValueFromSource: function readValueFromSource(key) {
          return nativeElement.getStyle(key);
        },
        // TODO: This is a good second source of plugins. This function contains the CSS variable
        // and unit conversion support. These functions share a common signature. We could make another
        // API for adding these.
        makeTargetAnimatable: parseDomVariant(values, nativeElement)
      };
    }
  };
}
/**
 * Uses the ref that is passed in, or creates a new one
 * @param external - External ref
 * @internal
 */


function useExternalRef(externalRef) {
  // We're conditionally calling `useRef` here which is sort of naughty as hooks
  // shouldn't be called conditionally. However, Framer Motion will break if this
  // condition changes anyway. It might be possible to use an invariant here to
  // make it explicit, but I expect changing `ref` is not normal behaviour.
  var ref = !externalRef || typeof externalRef === "function" ? useRef(null) : externalRef; // Handle `ref` functions. Again, calling the hook conditionally is kind of naughty
  // but `ref` types changing between renders would break Motion anyway. If we receive
  // bug reports about this, we should track the provided ref and throw an invariant
  // rather than move the conditional to inside the useEffect as this will be fired
  // for every Frame component within Framer.

  if (externalRef && typeof externalRef === "function") {
    useEffect(function () {
      externalRef(ref.current);
      return function () {
        return externalRef(null);
      };
    }, []);
  }

  return ref;
}

var htmlMotionComponents = htmlElements.reduce(function (acc, Component) {
  var config = createDomMotionConfig(Component); // Suppress "Expression produces a union type that is too complex to represent" error
  // @ts-ignore

  acc[Component] = createMotionComponent(config);
  return acc;
}, {});
var svgMotionComponents = svgElements.reduce(function (acc, Component) {
  // Suppress "Expression produces a union type that is too complex to represent" error
  // @ts-ignore
  acc[Component] = createMotionComponent(createDomMotionConfig(Component));
  return acc;
}, {});
/**
 * HTML & SVG components, optimised for use with gestures and animation. These can be used as
 * drop-in replacements for any HTML & SVG component, all CSS & SVG properties are supported.
 *
 * @internalremarks
 *
 * I'd like to make it possible for these to be loaded "on demand" - to reduce bundle size by only
 * including HTML/SVG stylers, animation and/or gesture support when necessary.
 *
 * ```jsx
 * <motion.div animate={{ x: 100 }} />
 *
 * <motion.p animate={{ height: 200 }} />
 *
 * <svg><motion.circle r={10} animate={{ r: 20 }} /></svg>
 * ```
 *
 * @public
 */

var motion = __assign(__assign({
  /**
   * Convert a custom React component into a `motion` component.
   *
   * It can also accept a string, to create [custom DOM elements](https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements).
   *
   * ```jsx
   * const Component = React.forwardRef((props: Props, ref) => {
   *   return <div ref={ref} />
   * })
   *
   * const MotionComponent = motion.custom<Props>(Component)
   * ```
   *
   * @param Component -
   */
  custom: function custom(Component) {
    return createMotionComponent(createDomMotionConfig(Component));
  }
}, htmlMotionComponents), svgMotionComponents);

var isCustomValueType = function isCustomValueType(v) {
  return typeof v === "object" && v.mix;
};

var getMixer = function getMixer(v) {
  return isCustomValueType(v) ? v.mix : undefined;
};

function transform() {
  var args = [];

  for (var _i = 0; _i < arguments.length; _i++) {
    args[_i] = arguments[_i];
  }

  var useImmediate = !Array.isArray(args[0]);
  var argOffset = useImmediate ? 0 : -1;
  var inputValue = args[0 + argOffset];
  var inputRange = args[1 + argOffset];
  var outputRange = args[2 + argOffset];
  var options = args[3 + argOffset];
  var interpolator = interpolate(inputRange, outputRange, __assign({
    mixer: getMixer(outputRange[0])
  }, options));
  return useImmediate ? interpolator(inputValue) : interpolator;
}

var isTransformer = function isTransformer(v) {
  return typeof v === "function";
};

function useTransform(parent, customTransform, to, options) {
  var _a;

  var comparitor = isTransformer(customTransform) ? [parent] : [parent, customTransform.join(","), (_a = to) === null || _a === void 0 ? void 0 : _a.join(",")];
  var transformer = useMemo(function () {
    return isTransformer(customTransform) ? customTransform : transform(customTransform, to, options);
  }, comparitor);
  var initialValue = transformer(parent.get());
  var value = useMotionValue(initialValue); // Handle subscription to parent

  var unsubscribe = useRef();
  useMemo(function () {
    unsubscribe.current && unsubscribe.current();
    unsubscribe.current = parent.onChange(function (v) {
      return value.set(transformer(v));
    }); // Manually set with the latest parent value in case we've re-parented

    value.set(initialValue);
  }, [parent, value, transformer]);
  useUnmountEffect(function () {
    return unsubscribe.current && unsubscribe.current();
  });
  return value;
} // Keep things reasonable and avoid scale: Infinity. In practise we might need
// to add another value, opacity, that could interpolate scaleX/Y [0,0.01] => [0,1]
// to simply hide content at unreasonable scales.


var maxScale = 100000;

var invertScale = function invertScale(scale) {
  return scale > 0.001 ? 1 / scale : maxScale;
};
/**
 * Returns a `MotionValue` each for `scaleX` and `scaleY` that update with the inverse
 * of their respective parent scales.
 *
 * This is useful for undoing the distortion of content when scaling a parent component.
 *
 * By default, `useInvertedScale` will automatically fetch `scaleX` and `scaleY` from the nearest parent.
 * By passing other `MotionValue`s in as `useInvertedScale({ scaleX, scaleY })`, it will invert the output
 * of those instead.
 *
 * @motion
 *
 * ```jsx
 * const MyComponent = () => {
 *   const { scaleX, scaleY } = useInvertedScale()
 *   return <motion.div style={{ scaleX, scaleY }} />
 * }
 * ```
 *
 * @library
 *
 * ```jsx
 * function MyComponent() {
 *   const { scaleX, scaleY } = useInvertedScale()
 *   return <Frame scaleX={scaleX} scaleY={scaleY} />
 * }
 * ```
 *
 * @public
 */


function useInvertedScale(scale) {
  var parentScaleX = useMotionValue(1);
  var parentScaleY = useMotionValue(1);
  var values = useContext(MotionContext).values;
  invariant(!!(scale || values), "If no scale values are provided, useInvertedScale must be used within a child of another motion component.");

  if (scale) {
    parentScaleX = scale.scaleX || parentScaleX;
    parentScaleY = scale.scaleY || parentScaleY;
  } else if (values) {
    parentScaleX = values.get("scaleX", 1);
    parentScaleY = values.get("scaleY", 1);
  }

  var scaleX = useTransform(parentScaleX, invertScale);
  var scaleY = useTransform(parentScaleY, invertScale);
  return {
    scaleX: scaleX,
    scaleY: scaleY
  };
}

function useOnChange(value, callback) {
  useEffect(function () {
    return isMotionValue(value) ? value.onChange(callback) : undefined;
  }, [value]);
}
/**
 * Creates a `MotionValue` that, when `set`, will use a spring animation to animate to its new state.
 *
 * It can either work as a stand-alone `MotionValue` by initialising it with a value, or as a subscriber
 * to another `MotionValue`.
 *
 * @remarks
 *
 * ```jsx
 * const x = useSpring(0, { stiffness: 300 })
 * const y = useSpring(x, { damping: 10 })
 * ```
 *
 * @param inputValue - `MotionValue` or number. If provided a `MotionValue`, when the input `MotionValue` changes, the created `MotionValue` will spring towards that value.
 * @param springConfig - Configuration options for the spring.
 * @returns `MotionValue`
 *
 * @public
 */


function useSpring(source, config) {
  if (config === void 0) {
    config = {};
  }

  var activeSpringAnimation = useRef(null);
  var value = useMotionValue(isMotionValue(source) ? source.get() : source);
  useMemo(function () {
    return value.attach(function (v, set) {
      if (activeSpringAnimation.current) {
        activeSpringAnimation.current.stop();
      }

      activeSpringAnimation.current = spring(__assign({
        from: value.get(),
        to: v,
        velocity: value.getVelocity()
      }, config)).start(set);
      return value.get();
    });
  }, Object.values(config));
  useOnChange(source, function (v) {
    return value.set(parseFloat(v));
  });
  return value;
}

var scrollX = motionValue(0);
var scrollY = motionValue(0);
var scrollXProgress = motionValue(0);
var scrollYProgress = motionValue(0);

var setProgress = function setProgress(offset, maxOffset, value) {
  value.set(!maxOffset || !offset ? 0 : offset / maxOffset);
};

var hasEventListener = false;

var addScrollListener = function addScrollListener() {
  hasEventListener = true;
  if (typeof window === "undefined") return;

  var updateScrollValues = function updateScrollValues() {
    var xOffset = window.pageXOffset;
    var yOffset = window.pageYOffset; // Set absolute positions

    scrollX.set(xOffset);
    scrollY.set(yOffset); // Set 0-1 progress

    setProgress(xOffset, document.body.clientWidth - window.innerWidth, scrollXProgress);
    setProgress(yOffset, document.body.clientHeight - window.innerHeight, scrollYProgress);
  };

  updateScrollValues();
  window.addEventListener("resize", updateScrollValues);
  window.addEventListener("scroll", updateScrollValues, {
    passive: true
  });
};

var viewportMotionValues = {
  scrollX: scrollX,
  scrollY: scrollY,
  scrollXProgress: scrollXProgress,
  scrollYProgress: scrollYProgress
};
/**
 * Provides `MotionValue`s that update when the viewport scrolls:
 *
 * - `scrollX` — Horizontal scroll distance in pixels.
 * - `scrollY` — Vertical scroll distance in pixels.
 * - `scrollXProgress` — Horizontal scroll progress between `0` and `1`.
 * - `scrollYProgress` — Vertical scroll progress between `0` and `1`.
 *
 * **Note:** If the returned scroll `MotionValue`s don't seem to be updating,
 * double check if the `body` tag styles are set to `width: 100%; height: 100%` or
 * similar, as this can break accurate measurement of viewport scroll.
 *
 * @library
 *
 * ```jsx
 * import * as React from "react"
 * import {
 *   Frame,
 *   useViewportScroll,
 *   useTransform
 * } from "framer"
 *
 * export function MyComponent() {
 *   const { scrollYProgress } = useViewportScroll()
 *   return <Frame scaleX={scrollYProgress} />
 * }
 * ```
 *
 * @motion
 *
 * ```jsx
 * export const MyComponent = () => {
 *   const { scrollYProgress } = useViewportScroll()
 *   return <motion.div style={{ scaleX: scrollYProgress }} />
 * }
 * ```
 *
 * @internalremarks
 * This isn't technically a hook yet, but in the future it might be nice
 * to accept refs to elements and add scroll listeners to those, which
 * may involve the use of lifecycle.
 *
 * @public
 */

function useViewportScroll() {
  if (!hasEventListener) {
    addScrollListener();
  }

  return viewportMotionValues;
}
/**
 * Creates `AnimationControls`, which can be used to manually start, stop
 * and sequence animations on one or more components.
 *
 * The returned `AnimationControls` should be passed to the `animate` property
 * of the components you want to animate.
 *
 * These components can then be animated with the `start` method.
 *
 * @library
 *
 * ```jsx
 * import * as React from 'react'
 * import { Frame, useAnimation } from 'framer'
 *
 * export function MyComponent(props) {
 *    const controls = useAnimation()
 *
 *    controls.start({
 *        x: 100,
 *        transition: { duration: 0.5 },
 *    })
 *
 *    return <Frame animate={controls} />
 * }
 * ```
 *
 * @motion
 *
 * ```jsx
 * import * as React from 'react'
 * import { motion, useAnimation } from 'framer-motion'
 *
 * export function MyComponent(props) {
 *    const controls = useAnimation()
 *
 *    controls.start({
 *        x: 100,
 *        transition: { duration: 0.5 },
 *    })
 *
 *    return <motion.div animate={controls} />
 * }
 * ```
 *
 * @returns Animation controller with `start` and `stop` methods
 *
 * @public
 */


function useAnimation() {
  var animationControls = useConstant(function () {
    return new AnimationControls();
  });
  useEffect(function () {
    animationControls.mount();
    return function () {
      return animationControls.unmount();
    };
  }, []);
  return animationControls;
}
/**
 * Experimental API.
 *
 * Makes an animated version of `useState`.
 *
 * @remarks
 *
 * When the returned state setter is called, values will be animated to their new target.
 *
 * This allows the animation of arbitrary React components.
 *
 * **Note:** When animating DOM components, it's always preferable to use the `animate` prop, as Framer
 * will bypass React's rendering cycle with one optimised for 60fps motion. This Hook is specifically
 * for animating props on arbitrary React components, or for animating text content.
 *
 * ```jsx
 * const [state, setState] = useAnimatedState({ percentage: 0 })
 *
 * return (
 *   <Graph
 *     percentage={state.percentage}
 *     onTap={() => setState({ percentage: 50 })}
 *   />
 * )
 * ```
 *
 * @internalremarks
 *
 * TODO:
 * - Make hook accept a typed version of Target that accepts any value (not just DOM values)
 * - Allow hook to accept single values. ie useAnimatedState(0)
 * - Allow providing MotionValues via initialState.
 *
 * @beta
 */


function useAnimatedState(initialState) {
  var _a = useState(initialState),
      animationState = _a[0],
      onUpdate = _a[1];

  var config = useConstant(function () {
    return {
      onUpdate: onUpdate
    };
  });
  var values = useMotionValues(config);
  var controls = useValueAnimationControls({
    values: values,
    readValueFromSource: function readValueFromSource(key) {
      return animationState[key];
    }
  }, {}, false);
  var startAnimation = useConstant(function () {
    return function (animationDefinition) {
      return controls.start(animationDefinition);
    };
  });
  useEffect(function () {
    values.mount();
    return function () {
      return values.unmount();
    };
  }, []);
  return [animationState, startAnimation];
}
/**
 * Cycles through a series of visual properties. Can be used to toggle between or cycle through animations. It works similar to `useState` in React. It is provided an initial array of possible states, and returns an array of two arguments.
 *
 * @library
 *
 * ```jsx
 * import * as React from "react"
 * import { Frame, useCycle } from "framer"
 *
 * export function MyComponent() {
 *   const [x, cycleX] = useCycle(0, 50, 100)
 *
 *   return (
 *     <Frame
 *       animate={{ x: x }}
 *       onTap={() => cycleX()}
 *      />
 *    )
 * }
 * ```
 *
 * @motion
 *
 * An index value can be passed to the returned `cycle` function to cycle to a specific index.
 *
 * ```jsx
 * import * as React from "react"
 * import { motion, useCycle } from "framer-motion"
 *
 * export const MyComponent = () => {
 *   const [x, cycleX] = useCycle(0, 50, 100)
 *
 *   return (
 *     <motion.div
 *       animate={{ x: x }}
 *       onTap={() => cycleX()}
 *      />
 *    )
 * }
 * ```
 *
 * @param items - items to cycle through
 * @returns [currentState, cycleState]
 *
 * @public
 */


function useCycle() {
  var items = [];

  for (var _i = 0; _i < arguments.length; _i++) {
    items[_i] = arguments[_i];
  } // TODO: After Framer X beta, remove this warning


  warning(items.length > 1, "useCycle syntax has changed. `useCycle([0, 1, 2])` becomes `useCycle(0, 1, 2)`");
  var index = useRef(0);

  var _a = useState(items[index.current]),
      item = _a[0],
      setItem = _a[1];

  return [item, function (next) {
    index.current = typeof next !== "number" ? wrap(0, items.length, index.current + 1) : next;
    setItem(items[index.current]);
  }];
}
/**
 * Can manually trigger a drag gesture on one or more `drag`-enabled `motion` components.
 *
 * @library
 *
 * ```jsx
 * const dragControls = useDragControls()
 *
 * function startDrag(event) {
 *   dragControls.start(event, { snapToCursor: true })
 * }
 *
 * return (
 *   <>
 *     <Frame onTapStart={startDrag} />
 *     <Frame drag="x" dragControls={dragControls} />
 *   </>
 * )
 * ```
 *
 * @motion
 *
 * ```jsx
 * const dragControls = useDragControls()
 *
 * function startDrag(event) {
 *   dragControls.start(event, { snapToCursor: true })
 * }
 *
 * return (
 *   <>
 *     <div onMouseDown={startDrag} />
 *     <motion.div drag="x" dragControls={dragControls} />
 *   </>
 * )
 * ```
 *
 * @public
 */


var DragControls =
/** @class */
function () {
  function DragControls() {
    this.componentControls = new Set();
  }
  /**
   * Subscribe a component's internal `ComponentDragControls` to the user-facing API.
   *
   * @internal
   */


  DragControls.prototype.subscribe = function (controls) {
    var _this = this;

    this.componentControls.add(controls);
    return function () {
      return _this.componentControls["delete"](controls);
    };
  };
  /**
   * Start a drag gesture on every `motion` component that has this set of drag controls
   * passed into it via the `dragControls` prop.
   *
   * ```jsx
   * dragControls.start(e, {
   *   snapToCursor: true
   * })
   * ```
   *
   * @param event - A mouse/touch/pointer event.
   * @param options - Options
   *
   * @public
   */


  DragControls.prototype.start = function (event, options) {
    this.componentControls.forEach(function (controls) {
      controls.start(event.nativeEvent || event, options);
    });
  };

  return DragControls;
}();

var createDragControls = function createDragControls() {
  return new DragControls();
};
/**
 * Usually, dragging is initiated by pressing down on a `motion` component with a `drag` prop
 * and moving it. For some use-cases, for instance clicking at an arbitrary point on a video scrubber, we
 * might want to initiate that dragging from a different component than the draggable one.
 *
 * By creating a `dragControls` using the `useDragControls` hook, we can pass this into
 * the draggable component's `dragControls` prop. It exposes a `start` method
 * that can start dragging from pointer events on other components.
 *
 * @library
 *
 * ```jsx
 * const dragControls = useDragControls()
 *
 * function startDrag(event) {
 *   dragControls.start(event, { snapToCursor: true })
 * }
 *
 * return (
 *   <>
 *     <Frame onTapStart={startDrag} />
 *     <Frame drag="x" dragControls={dragControls} />
 *   </>
 * )
 * ```
 *
 * @motion
 *
 * ```jsx
 * const dragControls = useDragControls()
 *
 * function startDrag(event) {
 *   dragControls.start(event, { snapToCursor: true })
 * }
 *
 * return (
 *   <>
 *     <div onMouseDown={startDrag} />
 *     <motion.div drag="x" dragControls={dragControls} />
 *   </>
 * )
 * ```
 *
 * @public
 */


function useDragControls() {
  return useConstant(createDragControls);
}

function useForceUpdate() {
  var _a = useState(0),
      forcedRenderCount = _a[0],
      setForcedRenderCount = _a[1];

  return useCallback(function () {
    return setForcedRenderCount(forcedRenderCount + 1);
  }, [forcedRenderCount]);
}

var PresenceChild = function PresenceChild(_a) {
  var children = _a.children,
      initial = _a.initial,
      isPresent = _a.isPresent,
      _onExitComplete = _a.onExitComplete,
      custom = _a.custom;
  var numPresenceChildren = useRef(0);
  var numExitComplete = useRef(0);
  var context = {
    initial: initial,
    isPresent: isPresent,
    custom: custom,
    onExitComplete: function onExitComplete() {
      numExitComplete.current++;
      var allComplete = numExitComplete.current >= numPresenceChildren.current;
      _onExitComplete && allComplete && _onExitComplete();
    }
  };
  var register = useMemo(function () {
    numExitComplete.current = 0;
    return function () {
      numPresenceChildren.current++;
      return function () {
        return numPresenceChildren.current--;
      };
    };
  }, [isPresent]);
  return createElement(PresenceContext.Provider, {
    value: __assign(__assign({}, context), {
      register: register
    })
  }, children);
};

function getChildKey(child) {
  return child.key || "";
}

function updateChildLookup(children, allChildren) {
  var seenChildren = process.env.NODE_ENV !== "production" ? new Set() : null;
  children.forEach(function (child) {
    var key = getChildKey(child);

    if (process.env.NODE_ENV !== "production" && seenChildren) {
      if (seenChildren.has(key)) {
        console.warn("Children of AnimatePresence require unique keys. \"" + key + "\" is a duplicate.");
      }

      seenChildren.add(key);
    }

    allChildren.set(key, child);
  });
}

function onlyElements(children) {
  var filtered = []; // We use forEach here instead of map as map mutates the component key by preprending `.$`

  Children.forEach(children, function (child) {
    if (isValidElement(child)) filtered.push(child);
  });
  return filtered;
}
/**
 * `AnimatePresence` enables the animation of components that have been removed from the tree.
 *
 * When adding/removing more than a single child, every child **must** be given a unique `key` prop.
 *
 * @library
 *
 * Any `Frame` components that have an `exit` property defined will animate out when removed from
 * the tree.
 *
 * ```jsx
 * import { Frame, AnimatePresence } from 'framer'
 *
 * // As items are added and removed from `items`
 * export function Items({ items }) {
 *   return (
 *     <AnimatePresence>
 *       {items.map(item => (
 *         <Frame
 *           key={item.id}
 *           initial={{ opacity: 0 }}
 *           animate={{ opacity: 1 }}
 *           exit={{ opacity: 0 }}
 *         />
 *       ))}
 *     </AnimatePresence>
 *   )
 * }
 * ```
 *
 * You can sequence exit animations throughout a tree using variants.
 *
 * @motion
 *
 * Any `motion` components that have an `exit` property defined will animate out when removed from
 * the tree.
 *
 * ```jsx
 * import { motion, AnimatePresence } from 'framer-motion'
 *
 * export const Items = ({ items }) => (
 *   <AnimatePresence>
 *     {items.map(item => (
 *       <motion.div
 *         key={item.id}
 *         initial={{ opacity: 0 }}
 *         animate={{ opacity: 1 }}
 *         exit={{ opacity: 0 }}
 *       />
 *     ))}
 *   </AnimatePresence>
 * )
 * ```
 *
 * You can sequence exit animations throughout a tree using variants.
 *
 * If a child contains multiple `motion` components with `exit` props, it will only unmount the child
 * once all `motion` components have finished animating out. Likewise, any components using
 * `usePresence` all need to call `safeToRemove`.
 *
 * @public
 */


var AnimatePresence = function AnimatePresence(_a) {
  var children = _a.children,
      custom = _a.custom,
      _b = _a.initial,
      initial = _b === void 0 ? true : _b,
      onExitComplete = _a.onExitComplete,
      exitBeforeEnter = _a.exitBeforeEnter; // We want to force a re-render once all exiting animations have finished. We
  // either use a local forceRender function, or one from a parent context if it exists.

  var forceRender = useForceUpdate();
  var magicContext = useContext(SharedLayoutContext);

  if (isControlledSharedLayoutContext(magicContext)) {
    forceRender = magicContext.forceRender;
  }

  var isInitialRender = useRef(true); // Filter out any children that aren't ReactElements. We can only track ReactElements with a props.key

  var filteredChildren = onlyElements(children); // Keep a living record of the children we're actually rendering so we
  // can diff to figure out which are entering and exiting

  var presentChildren = useRef(filteredChildren); // A lookup table to quickly reference components by key

  var allChildren = useRef(new Map()).current; // A living record of all currently exiting components.

  var exiting = useRef(new Set()).current;
  updateChildLookup(filteredChildren, allChildren); // If this is the initial component render, just deal with logic surrounding whether
  // we play onMount animations or not.

  if (isInitialRender.current) {
    isInitialRender.current = false;
    return createElement(Fragment, null, filteredChildren.map(function (child) {
      return createElement(PresenceChild, {
        key: getChildKey(child),
        isPresent: true,
        initial: initial ? undefined : false
      }, child);
    }));
  } // If this is a subsequent render, deal with entering and exiting children


  var childrenToRender = __spreadArrays(filteredChildren); // Diff the keys of the currently-present and target children to update our
  // exiting list.


  var presentKeys = presentChildren.current.map(getChildKey);
  var targetKeys = filteredChildren.map(getChildKey); // Diff the present children with our target children and mark those that are exiting

  var numPresent = presentKeys.length;

  for (var i = 0; i < numPresent; i++) {
    var key = presentKeys[i];

    if (targetKeys.indexOf(key) === -1) {
      exiting.add(key);
    } else {
      // In case this key has re-entered, remove from the exiting list
      exiting["delete"](key);
    }
  } // If we currently have exiting children, and we're deferring rendering incoming children
  // until after all current children have exiting, empty the childrenToRender array


  if (exitBeforeEnter && exiting.size) {
    childrenToRender = [];
  } // Loop through all currently exiting components and clone them to overwrite `animate`
  // with any `exit` prop they might have defined.


  exiting.forEach(function (key) {
    // If this component is actually entering again, early return
    if (targetKeys.indexOf(key) !== -1) return;
    var child = allChildren.get(key);
    if (!child) return;
    var insertionIndex = presentKeys.indexOf(key);

    var onExit = function onExit() {
      exiting["delete"](key); // Remove this child from the present children

      var removeIndex = presentChildren.current.findIndex(function (presentChild) {
        return presentChild.key === key;
      });
      presentChildren.current.splice(removeIndex, 1); // Defer re-rendering until all exiting children have indeed left

      if (!exiting.size) {
        presentChildren.current = filteredChildren;
        forceRender();
        onExitComplete && onExitComplete();
      }
    };

    childrenToRender.splice(insertionIndex, 0, createElement(PresenceChild, {
      key: getChildKey(child),
      isPresent: false,
      onExitComplete: onExit,
      custom: custom
    }, child));
  }); // Add `MotionContext` even to children that don't need it to ensure we're rendering
  // the same tree between renders

  childrenToRender = childrenToRender.map(function (child) {
    var key = child.key;
    return exiting.has(key) ? child : createElement(PresenceChild, {
      key: getChildKey(child),
      isPresent: true
    }, child);
  });
  presentChildren.current = childrenToRender;

  if (process.env.NODE_ENV !== "production" && exitBeforeEnter && childrenToRender.length > 1) {
    console.warn("You're attempting to animate multiple children within AnimatePresence, but its exitBeforeEnter prop is set to true. This will lead to odd visual behaviour.");
  }

  return createElement(Fragment, null, exiting.size ? childrenToRender : childrenToRender.map(function (child) {
    return cloneElement(child);
  }));
};

function isControlledSharedLayoutContext(context) {
  return !!context.forceRender;
}

var defaultMagicTransition = {
  duration: 0.45,
  ease: [0.4, 0, 0.1, 1]
};
/**
 * @public
 */

var AnimateSharedLayout =
/** @class */
function (_super) {
  __extends(AnimateSharedLayout, _super);

  function AnimateSharedLayout() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    /**
     * Keep track of all animate children.
     */


    _this.children = new Set();
    /**
     * As animate components with a defined `layoutId` are added/removed to the tree,
     * we store them in order. When one is added, it will animate out from the
     * previous one, and when it's removed, it'll animate to the previous one.
     */

    _this.stacks = new Map();
    /**
     * Create an instance of the update batcher so we can run snapshots across
     * components and fire animate transitions in the correct order.
     */

    _this.batch = batchTransitions();
    /**
     * Keep one snapshot for each stack in the event the all layoutId children
     * are removed from a stack before the new one is added.
     */

    _this.snapshots = new Map();
    /**
     * We're tracking mount status as only subsequently-entering components need
     * tagging with `shouldResumeFromPrevious`.
     */

    _this.hasMounted = false;
    _this.state = {
      /**
       * Allow children, like AnimatePresence, to force-render this component
       * to ensure animate children correctly identify parallel state changes that
       * might affect their layout.
       */
      forceRender: function forceRender() {
        return _this.setState(__assign({}, _this.state));
      },
      register: function register(child) {
        return _this.addChild(child);
      }
    };
    return _this;
  }

  AnimateSharedLayout.prototype.componentDidMount = function () {
    this.hasMounted = true;
  };
  /**
   * We always want the component to re-render but for performance reasons we might
   * want to control whether we take snapshots and perform animate transitions.
   */


  AnimateSharedLayout.prototype.shouldComponentUpdate = function (nextProps, nextState) {
    this.shouldTransition = false;

    if (this.state !== nextState) {
      // This should always be true if we've got a new state as a result of a forced render
      this.shouldTransition = true;
    } else {
      // If we don't have a defined dependency, or we do and it's changed,
      // we want to transition
      var dependency = this.props.dependency;
      var hasDependency = !!(dependency !== null && dependency !== void 0 ? dependency : nextProps.dependency);
      var hasChanged = dependency !== nextProps.dependency;
      this.shouldTransition = !hasDependency || hasDependency && hasChanged;
    }
    /**
     * Hijacking this lifecycle method to reset rotation on any children so we
     * can properly measure the current bounding box. This isn't publicly supported as
     * this is naughty, but `rotate` is quite visible in Framer and needs some kind
     * of support.
     *
     * Although this might run multiple times in concurrent mode, this is
     * a write operation and getSnapshotBeforeUpdate is a read operation. So doing
     * it here avoids layout thrashing.
     */


    var supportRotate = this.props.supportRotate;

    if (supportRotate && this.shouldTransition) {
      this.children.forEach(function (child) {
        return child.resetRotation();
      });
    }

    return true;
  };
  /**
   * Loop through all children and update their latest origin snapshots.
   */


  AnimateSharedLayout.prototype.getSnapshotBeforeUpdate = function () {
    var _this = this;

    if (!this.shouldTransition) return null;
    this.children.forEach(function (child) {
      return child.snapshotOrigin();
    });
    /**
     * Every child keeps a local snapshot, but we also want to record
     * snapshots of the visible children as, if they're are being removed
     * in this render, we can still access them.
     */

    this.stacks.forEach(function (stack, key) {
      var latestChild = stack[stack.length - 1];
      latestChild && _this.snapshots.set(key, latestChild.measuredOrigin);
    });
    return null;
  };
  /**
   * Once all children have updated, snapshot their target snapshots and run
   * animate transitions.
   */


  AnimateSharedLayout.prototype.componentDidUpdate = function () {
    var _this = this;

    if (!this.shouldTransition) return;
    /**
     * Magic animations can be used without the AnimateSharedLayout wrapping component.
     * This requires some co-ordination across components to stop layout thrashing
     * and ensure measurements are taken at the correct time.
     *
     * Here we use that same mechanism of schedule/flush.
     */

    this.children.forEach(function (child) {
      return _this.batch.add(child);
    });
    var _a = this.props,
        type = _a.type,
        _b = _a.transition,
        transition = _b === void 0 ? defaultMagicTransition : _b;
    var options = {
      type: type,
      transition: transition
    };
    var handler = controlledHandler(options, this.rootDepth, this.stacks, this.snapshots);
    this.batch.flush(handler);
    this.snapshots.clear();
  };
  /**
   * Register a new `Magic` child
   */


  AnimateSharedLayout.prototype.addChild = function (child) {
    var _this = this;

    this.setRootDepth(child);
    this.children.add(child);
    this.addChildToStack(child);
    if (this.hasMounted) child.shouldResumeFromPrevious = true;
    return function () {
      return _this.removeChild(child);
    };
  };

  AnimateSharedLayout.prototype.addChildToStack = function (child) {
    var layoutId = child.props.layoutId;
    if (layoutId === undefined) return;
    var stack = this.getStack(layoutId);
    stack.push(child);
    var stackLength = stack.length;
    var previousChild = stack[stackLength - 2];
    previousChild && previousChild.hide();
  };

  AnimateSharedLayout.prototype.removeChild = function (child) {
    this.children["delete"](child);
    this.removeChildFromStack(child);
  };

  AnimateSharedLayout.prototype.removeChildFromStack = function (child) {
    var layoutId = child.props.layoutId;
    if (layoutId === undefined) return;
    var stack = this.getStack(layoutId);
    var childIndex = stack.findIndex(function (stackChild) {
      return child === stackChild;
    });
    if (childIndex === -1) return;
    stack.splice(childIndex, 1); // Set the previous child to visible

    var previousChild = stack[childIndex - 1];
    previousChild && previousChild.show();
  };
  /**
   * Return a stack of animate children based on the provided layoutId.
   * Will create a stack if none currently exists with that layoutId.
   */


  AnimateSharedLayout.prototype.getStack = function (id) {
    !this.stacks.has(id) && this.stacks.set(id, []);
    return this.stacks.get(id);
  };

  AnimateSharedLayout.prototype.setRootDepth = function (child) {
    this.rootDepth = this.rootDepth === undefined ? child.depth : Math.min(child.depth, this.rootDepth);
  };

  AnimateSharedLayout.prototype.render = function () {
    return createElement(SharedLayoutContext.Provider, {
      value: this.state
    }, this.props.children);
  };

  return AnimateSharedLayout;
}(Component);

function stackQuery(callback, fallback) {
  return function (child) {
    var layoutId = child.props.layoutId;
    return layoutId === undefined ? fallback : callback(layoutId, child);
  };
}

function compress(min, max, easing) {
  return function (p) {
    // Could replace ifs with clamp
    if (p < min) return 0;
    if (p > max) return 1;
    return easing(progress(min, max, p));
  };
}

function controlledHandler(_a, rootDepth, stacks, snapshots) {
  var transition = _a.transition,
      type = _a.type;
  var visible = new Map();
  var previous = new Map();
  stacks.forEach(function (stack, key) {
    var visibleIndex = stack.findIndex(function (child) {
      return child.isVisible;
    });
    if (visibleIndex === -1) return;
    visible.set(key, stack[visibleIndex]);
    var previousIndex = visibleIndex - 1;
    if (previousIndex === -1) return;
    previous.set(key, stack[previousIndex]);
  });
  var isVisibleInStack = stackQuery(function (layoutId, child) {
    return visible.get(layoutId) === child;
  }, true);
  var isPreviousInStack = stackQuery(function (layoutId, child) {
    return previous.get(layoutId) === child;
  }, false);
  var getPreviousOrigin = stackQuery(function (layoutId) {
    var _a;

    return ((_a = previous.get(layoutId)) === null || _a === void 0 ? void 0 : _a.measuredOrigin) || snapshots.get(layoutId);
  });
  var getPreviousTarget = stackQuery(function (layoutId) {
    var _a;

    return (_a = previous.get(layoutId)) === null || _a === void 0 ? void 0 : _a.measuredTarget;
  });
  var getVisibleOrigin = stackQuery(function (layoutId) {
    var _a;

    return (_a = visible.get(layoutId)) === null || _a === void 0 ? void 0 : _a.measuredOrigin;
  });
  var getVisibleTarget = stackQuery(function (layoutId) {
    var _a;

    return (_a = visible.get(layoutId)) === null || _a === void 0 ? void 0 : _a.measuredTarget;
  });
  var isVisibleInStackPresent = stackQuery(function (layoutId) {
    var visibleInStack = visible.get(layoutId);
    return visibleInStack && visibleInStack.isPresent();
  }, false);
  var isOnlyMemberOfStack = stackQuery(function (layoutId) {
    var stack = stacks.get(layoutId);
    return !stack ? true : stack.length <= 1;
  }, true);
  var getSnapshot = stackQuery(function (layoutId) {
    return snapshots.get(layoutId);
  });

  var isRootChild = function isRootChild(child) {
    return child.depth === rootDepth;
  };

  var crossfadeAnimation = function crossfadeAnimation(child) {
    var origin;
    var target;
    var crossfadeEasing;

    if (isVisibleInStack(child)) {
      if (child.isPresent() && child.shouldResumeFromPrevious) {
        // If this component is newly added and entering, animate out from
        // the previous component
        origin = getPreviousOrigin(child);

        if (isRootChild(child) && !(isOnlyMemberOfStack(child) && getSnapshot(child))) {
          crossfadeEasing = crossfadeIn;
          origin = opacity(origin || child.measuredTarget, 0);
        }
      } else if (!child.isPresent()) {
        // Or if this child is being removed, animate to the previous component
        target = getPreviousTarget(child);

        if (isRootChild(child)) {
          crossfadeEasing = crossfadeOut;
          target = opacity(target || child.measuredTarget, 0);
        }
      }
    } else if (isPreviousInStack(child)) {
      if (isVisibleInStackPresent(child)) {
        // If the visible child in this stack is present, animate this component to it
        target = getVisibleTarget(child);

        if (isRootChild(child)) {
          crossfadeEasing = crossfadeOut;
          target = opacity(target, 0);
        }
      } else {
        // If the visible child in this stack is being removed, animate from it
        origin = getVisibleOrigin(child);

        if (isRootChild(child)) {
          crossfadeEasing = crossfadeIn;
        }
      }
    }

    child.shouldResumeFromPrevious = false;
    child.startAnimation({
      origin: origin,
      target: target,
      transition: transition,
      crossfadeEasing: crossfadeEasing
    });
  };
  /**
   *
   */


  var switchAnimation = function switchAnimation(child) {
    if (isVisibleInStack(child)) {
      var origin_1;
      var target = void 0;

      if (child.isPresent()) {
        if (child.shouldResumeFromPrevious) {
          origin_1 = getPreviousOrigin(child);
        }

        if (child.shouldRestoreVisibility) {
          child.show(true);
          return;
        }
      } else {
        target = getPreviousTarget(child);
      }

      child.startAnimation({
        origin: origin_1,
        target: target,
        transition: transition
      });
    } else {
      if (isPreviousInStack(child)) child.hide(true);
      if (!child.isPresent()) child.safeToRemove();
    }

    child.shouldResumeFromPrevious = false;
  };

  return {
    snapshotTarget: function snapshotTarget(child) {
      if (isVisibleInStack(child) || isPreviousInStack(child)) {
        child.snapshotTarget();
      }
    },
    startAnimation: type === "crossfade" ? crossfadeAnimation : switchAnimation
  };
}

var crossfadeIn = compress(0, 0.5, circOut);
var crossfadeOut = compress(0.5, 0.95, linear);

function opacity(snapshot, value) {
  if (value === void 0) {
    value = 1;
  }

  if (!snapshot) return;
  return __assign(__assign({}, snapshot), {
    style: __assign(__assign({}, snapshot.style), {
      opacity: value
    })
  });
} // Does this device prefer reduced motion? Returns `null` server-side.


var prefersReducedMotion = motionValue(null);

if (typeof window !== "undefined") {
  if (window.matchMedia) {
    var motionMediaQuery_1 = window.matchMedia("(prefers-reduced-motion)");

    var setReducedMotionPreferences = function setReducedMotionPreferences() {
      return prefersReducedMotion.set(motionMediaQuery_1.matches);
    };

    motionMediaQuery_1.addListener(setReducedMotionPreferences);
    setReducedMotionPreferences();
  } else {
    prefersReducedMotion.set(false);
  }
}

function determineShouldReduceMotion(prefersReduced, isReducedMotion) {
  return typeof isReducedMotion === "boolean" ? isReducedMotion : Boolean(prefersReduced);
}
/**
 * A hook that returns `true` if we should be using reduced motion based on the current device's Reduced Motion setting.
 *
 * This can be used to implement changes to your UI based on Reduced Motion. For instance, replacing motion-sickness inducing
 * `x`/`y` animations with `opacity`, disabling the autoplay of background videos, or turning off parallax motion.
 *
 * It will actively respond to changes and re-render your components with the latest setting.
 *
 * ```jsx
 * export function Sidebar({ isOpem }) {
 *   const shouldReduceMotion = useReducedMotion()
 *   const closedX = shouldReduceMotion ? 0 : "-100%"
 *
 *   return (
 *     <motion.div animate={{
 *       opacity: isOpen ? 1 : 0,
 *       x: isOpen ? 0 : closedX
 *     }} />
 *   )
 * }
 * ```
 *
 * @return boolean
 *
 * @public
 */


function useReducedMotion() {
  var isReducedMotion = useContext(MotionContext).isReducedMotion;

  var _a = useState(determineShouldReduceMotion(prefersReducedMotion.get(), isReducedMotion)),
      shouldReduceMotion = _a[0],
      setShouldReduceMotion = _a[1];

  useEffect(function () {
    return prefersReducedMotion.onChange(function (v) {
      setShouldReduceMotion(determineShouldReduceMotion(v, isReducedMotion));
    });
  }, [setShouldReduceMotion, isReducedMotion]);
  return shouldReduceMotion;
}
/**
 * Define accessibility options for a tree. Can be used to force the tree into Reduced Motion mode,
 * or disable device detection.
 *
 * @internal
 */


function ReducedMotion(_a) {
  var children = _a.children,
      enabled = _a.enabled;
  var context = useContext(MotionContext);
  context = useMemo(function () {
    return __assign(__assign({}, context), {
      isReducedMotion: enabled
    });
  }, [enabled]);
  return createElement(MotionContext.Provider, {
    value: context
  }, children);
}

export { AnimatePresence, AnimateSharedLayout, AnimationControls, DragControls, MotionContext, MotionPluginContext, MotionPlugins, MotionValue, Point, ReducedMotion, SharedLayoutContext, animationControls, createMotionComponent, isValidMotionProp, motion, motionValue, resolveMotionValue, transform, useAnimatedState, useAnimation, useCycle, useDomEvent, useDragControls, useExternalRef, useGestures, useInvertedScale, useMotionValue, usePanGesture, usePresence, useReducedMotion, useSpring, useTapGesture, useTransform, useViewportScroll };